<template>
    <div>
        <b-overlay :show="showSpinner">
            <validation-observer ref="validationObserver" v-slot="vo">
                <!-- Quote Number -->
                <b-form-row v-if="!isUuidNullOrEmpty(quote.quoteId)">
                    <b-form-group label="Quote Number"
                                  label-for="quoteNumberDisplayText"
                                  class="col-md-4">
                        <b-form-input name="quoteNumberDisplayText"
                                      maxlength="500"
                                      v-model="quote.quoteNumberDisplayText"
                                      disabled>
                        </b-form-input>
                    </b-form-group>

                    <b-form-group label="Quote Status"
                                  label-for="quoteStatus"
                                  class="col-md-2">
                        <b-form-input name="quoteStatus"
                                      v-model="quote.quoteStatus"
                                      disabled>
                        </b-form-input>
                    </b-form-group>

                    <b-form-group label="Cancellation Reason"
                                  label-for="cancellationReason"
                                  v-if="cancelled"
                                  class="col-md-2">
                        <b-form-input name="cancellationReason"
                                      v-model="quote.quoteCancelledReasonDisplayName"
                                      disabled>
                        </b-form-input>
                    </b-form-group>

                    <b-form-group label="Cancelled By"
                                  label-for="cancelledBy"
                                  v-if="cancelled"
                                  class="col-md-2">
                        <b-form-input name="cancelledBy"
                                      v-model="quote.disabledByUserAccountDisplayName"
                                      disabled>
                        </b-form-input>
                    </b-form-group>

                    <b-form-group label="Date Cancelled"
                                  label-for="dateCancelled"
                                  v-if="cancelled"
                                  class="col-md-2">
                        <b-form-input name="dateCancelled"
                                      :value="quote.disabledDateTimeUTC | formatDateTime('DD/MM/YYYY h:mm A')"
                                      readonly>
                        </b-form-input>
                    </b-form-group>
                </b-form-row>

                <!-- Ordering Clients -->
                <b-form-row v-if="(finalized || cancelled) && (quote.clients == null || quote.clients.length <= 1)">
                    <validation-provider ref="clientValidator" rules="required" name="Ordering Client" v-slot="vp" slim>
                        <b-form-group label="Ordering Client"
                                      label-for="client"
                                      class="col-md-4">
                            <client-select ref="clientSelect"
                                           name="clientId"
                                           :disabled="(finalized || cancelled || !isUuidNullOrEmpty(quote.quoteId))"
                                           v-model="quote.clientId"
                                           :initial-clients="clientOptions"
                                           :requiredCourtRegistryId="(initialQuote != null) && (initialQuote.courtRegistryId == quote.courtRegistryId) ? initialQuote.courtRegistryId : null"
                                           :state="vp.errors[0] ? false : null"
                                           v-on:change="clientSelected">
                            </client-select>
                            <b-form-invalid-feedback>{{ vp.errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                    <validation-provider ref="clientBranchValidator" rules="required" name="Client Branch" v-slot="vp" slim>
                        <b-form-group label="Ordering Client Branch"
                                      label-for="clientBranchId"
                                      class="col-md-4">
                            <branch-select ref="clientBranchSelect"
                                           name="clientBranchId"
                                           :disabled="finalized || cancelled || (quote.clientId == null)"
                                           :client-id="quote.clientId"
                                           v-model="quote.clientBranchId"
                                           :initial-branches="initialClientBranches"
                                           :state="isUuidNullOrEmpty(quote.clientId) ? null : vp.errors[0] ? false : null">
                            </branch-select>
                            <b-form-invalid-feedback :state="isUuidNullOrEmpty(quote.clientId) ? null : vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                    <validation-provider ref="clientContactValidator" rules="required" name="Ordering Client Contact" v-slot="vp" slim>
                        <b-form-group label="Ordering Client Contact"
                                      label-for="quote.clientContactId"
                                      class="col-md-4">
                            <contact-select name="quote.clientContactId"
                                            :disabled="finalized || cancelled || (quote.clientId == null)"
                                            :api-url="apiControllerUrl('contact/client')"
                                            :ref-data-api-controller-url="apiControllerUrl('referencedata')"
                                            :client-id="quote.clientId"
                                            v-model="quote.clientContactId"
                                            :initial-contacts="initialClientContacts"
                                            :state="isUuidNullOrEmpty(quote.clientId) ? null : vp.errors[0] ? false : null">
                            </contact-select>
                            <b-form-invalid-feedback :state="isUuidNullOrEmpty(quote.clientId) ? null : vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                </b-form-row>

                <b-form-row v-else>
                    <div class="card mt-2 mb-4">
                        <div class="card-header d-flex justify-content-between align-items-start">
                            <h5 class="card-title">Ordering Clients</h5>
                            <b-dropdown id="addClientButton" size="sm" variant="outline-secondary" split right v-on:click="addClient" v-if="!finalized && !cancelled">
                                <template v-slot:button-content>
                                    <i class="fas fa-plus mr-1"></i>Add
                                </template>
                                <b-dropdown-item v-on:click="resetDefaultSplitPercentages">Reset Split %</b-dropdown-item>
                                <b-dropdown-item v-on:click="clearDefaultSplitPercentages">Clear Split %</b-dropdown-item>
                            </b-dropdown>
                        </div>
                        <b-table striped hover fixed small :items="quote.clients" :fields="clientFields" class="mb-0">
                            <template #cell(action)="row">
                                <b-button size="sm" variant="secondary" v-on:click="editClient(row.item)" v-if="row.item.clientId && !finalized && !cancelled">
                                    Edit
                                </b-button>
                                <b-button size="sm" variant="danger" v-on:click="removeClient(row.item)" v-if="row.item.clientId && !finalized && !cancelled">
                                    Remove
                                </b-button>
                                <b-button size="sm" variant="outline-primary" v-on:click="printNewQuote(row.item)" v-if="row.item.clientId && finalized">
                                    <i class="far fa-file-pdf mr-1"></i>PDF
                                </b-button>
                            </template>
                        </b-table>

                        <validation-observer v-slot="vp" slim>
                            <b-modal size="md"
                                     centered
                                     :title="(currentClient.originalItem == null) ? 'Add Ordering Client' : 'Edit Ordering Client'"
                                     :ok-title="(currentClient.originalItem == null) ? 'Add' : 'OK'"
                                     cancel-title="Cancel"
                                     v-model="addOrEditClientModalShown"
                                     v-on:ok.prevent="vp.handleSubmit(createOrUpdateClient)">

                                <b-form-row>
                                    <validation-provider ref="clientValidator" rules="required" name="Ordering Client" v-slot="vp" slim>
                                        <b-form-group label="Ordering Client"
                                                      label-for="clientId"
                                                      class="col-md-12">
                                            <client-select ref="clientSelect"
                                                           name="clientId"
                                                           in-b-modal
                                                           v-model="currentClient.clientId"
                                                           :initial-clients="clientOptions"
                                                           :requiredCourtRegistryId="(initialQuote == null) && (quote.courtRegistryId != null) ? quote.courtRegistryId : null"
                                                           :state="vp.errors[0] ? false : null"
                                                           v-on:change="clientSelected">
                                            </client-select>
                                            <b-form-invalid-feedback>{{ vp.errors[0] }}</b-form-invalid-feedback>
                                        </b-form-group>
                                    </validation-provider>
                                </b-form-row>
                                <b-form-row>
                                    <validation-provider ref="clientBranchValidator" rules="required" name="Client Branch" v-slot="vp" slim>
                                        <b-form-group label="Ordering Client Branch"
                                                      label-for="clientBranchId"
                                                      class="col-md-12">
                                            <branch-select ref="clientBranchSelect"
                                                           name="clientBranchId"
                                                           :disabled="finalized || cancelled || (currentClient.clientId == null)"
                                                           :client-id="currentClient.clientId"
                                                           in-b-modal
                                                           v-model="currentClient.clientBranchId"
                                                           :initial-branches="clientBranchOptions"
                                                           :state="isUuidNullOrEmpty(currentClient.clientId) ? null : vp.errors[0] ? false : null">
                                            </branch-select>
                                            <b-form-invalid-feedback :state="isUuidNullOrEmpty(currentClient.clientId) ? null : vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                                        </b-form-group>
                                    </validation-provider>
                                </b-form-row>
                                <b-form-row>
                                    <validation-provider ref="clientContactValidator" rules="required" name="Ordering Client Contact" v-slot="vp" slim>
                                        <b-form-group label="Ordering Client Contact"
                                                      label-for="clientContactId"
                                                      class="col-md-12">
                                            <contact-select ref="clientContactSelect"
                                                            name="clientContactId"
                                                            :disabled="finalized || cancelled || (currentClient.clientId == null)"
                                                            :api-url="apiControllerUrl('contact/client')"
                                                            :ref-data-api-controller-url="apiControllerUrl('referencedata')"
                                                            :client-id="currentClient.clientId"
                                                            in-b-modal
                                                            v-model="currentClient.clientContactId"
                                                            :initial-contacts="clientContactOptions"
                                                            :state="isUuidNullOrEmpty(currentClient.clientId) ? null : vp.errors[0] ? false : null">
                                            </contact-select>
                                            <b-form-invalid-feedback :state="isUuidNullOrEmpty(currentClient.clientId) ? null : vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                                        </b-form-group>
                                    </validation-provider>
                                </b-form-row>
                                <b-form-row>
                                    <validation-provider ref="defaultPercentageValidationProvider"
                                                         :rules="{ min_value:0, max_value:maxDefaultSplitPercentage }"
                                                         name="Default Split %"
                                                         v-slot="{ validate, errors }"
                                                         class="col-md-6">
                                        <b-form-group label="Default Split %"
                                                      label-for="defaultSplitPercentage">
                                            <b-form-input name="defaultSplitPercentage"
                                                          type="number"
                                                          number
                                                          :min="0"
                                                          :max="maxDefaultSplitPercentage"
                                                          v-model="currentClient.defaultSplitPercentage"
                                                          :state="errors[0] ? false : null"
                                                          class="col-md-6">
                                            </b-form-input>
                                            <b-form-invalid-feedback :state="errors[0] ? false : null">{{ errors[0] }}</b-form-invalid-feedback>
                                        </b-form-group>
                                    </validation-provider>
                                </b-form-row>
                            </b-modal>
                        </validation-observer>
                    </div>
                </b-form-row>

                <!-- Court/Tribunal -->
                <b-form-row>
                    <validation-provider ref="courtRegistryValidator" rules="required" name="Court/Tribunal/Other" v-slot="vp" slim>
                        <b-form-group label="Court/Tribunal/Other"
                                      label-for="courtRegistryId"
                                      class="col-md-4">
                            <select2 name="courtRegistryId"
                                     :disabled="finalized || cancelled"
                                     :options="initialCourtRegistries"
                                     enable-ajax
                                     allow-clear
                                     :placeholder="'Select a court/tribunal/other'"
                                     key-property="courtRegistryId"
                                     text-property="name"
                                     v-model="quote.courtRegistryId"
                                     v-on:search="findCourtRegistries"
                                     v-on:select="courtRegistrySelected"
                                     :state="isUuidNullOrEmpty(quote.clientId) ? null : vp.errors[0] ? false : null">
                            </select2>
                            <b-form-invalid-feedback :state="isUuidNullOrEmpty(quote.clientId) ? null : vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                    <b-form-group label="Jurisdiction"
                                  label-for="quote.courtJurisdictionId"
                                  class="col-md-4">
                        <select2 name="courtJurisdictionId"
                                 :options="courtJuristictionOptions"
                                 :placeholder="(quote.courtRegistryId == null) ? 'Select a court/tribunal/other first' : 'Select a jurisdiction'"
                                 :disabled="finalized || cancelled || (quote.courtRegistryId == null)"
                                 v-model="quote.courtJurisdictionId"
                                 key-property="courtJurisdictionId"
                                 text-property="name"
                                 allowClear>
                        </select2>
                    </b-form-group>
                    <b-form-group label="Location"
                                  label-for="location"
                                  class="col-md-4">
                        <b-form-input name="location"
                                      maxlength="500"
                                      v-model="quote.location"
                                      :disabled="finalized || cancelled">
                        </b-form-input>
                    </b-form-group>
                </b-form-row>

                <!-- Matter -->
                <b-form-row>
                    <b-form-group :label="$t('Entities.Shared.MatterNumber')"
                                  label-for="matterNumber"
                                  class="col-md-4">
                        <b-form-input name="matterNumber"
                                      maxlength="50"
                                      v-model="quote.matterNumber"
                                      :disabled="finalized || cancelled">
                        </b-form-input>
                    </b-form-group>
                    <b-form-group :label="$t('Entities.Shared.MatterName') + ' or Title'"
                                  label-for="matterName"
                                  class="col-md-8">
                        <b-form-input name="matterName"
                                      maxlength="500"
                                      v-model="quote.matterName"
                                      :disabled="finalized || cancelled">
                        </b-form-input>
                    </b-form-group>
                </b-form-row>

                <!-- Service Type -->
                <b-form-row>
                    <validation-provider ref="serviceTypeValidator" rules="required" name="Service Type" v-slot="vp" slim>
                        <b-form-group label="Service Type"
                                      label-for="serviceTypeId"
                                      class="col-md-6">
                            <select2 ref="serviceTypeSelect"
                                     name="serviceTypeId"
                                     v-model="quote.serviceTypeId"
                                     :options="serviceTypeOptions"
                                     key-property="serviceTypeId"
                                     text-property="name"
                                     :placeholder="(quote.courtRegistryId == null) ? 'Select a court/tribunal/other first' : 'Select a service type'"
                                     :disabled="finalized || cancelled || (quote.courtRegistryId == null)"
                                     :state="vp.errors[0] ? false : null"
                                     v-on:select="serviceTypeSelected">
                            </select2>
                            <b-form-invalid-feedback>{{ vp.errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>

                    <!--<validation-provider ref="serviceDelayValidator" :rules="{required_if: selectedServiceTypeRequiresServiceDelay}" name="Turnaround" v-slot="vp" slim>-->
                    <validation-provider ref="serviceDelayValidator"
                                         name="Turnaround" v-slot="vp" slim
                                         :rules="{required_if: selectedServiceTypeRequiresServiceDelay}"
                                         v-if="selectedServiceTypeRequiresServiceDelay">

                        <b-form-group label="Turnaround"
                                      label-for="serviceDelayId"
                                      class="col-md-6"
                                      v-if="selectedServiceTypeRequiresServiceDelay">
                            <select2 ref="serviceDelaySelect"
                                     name="serviceDelayId"
                                     v-model="quote.serviceDelayId"
                                     :options="serviceDelayOptions"
                                     key-property="serviceDelayId"
                                     text-property="name"
                                     placeholder="Select a turnaround"
                                     :disabled="finalized || cancelled"
                                     :state="vp.errors[0] ? false : null"
                                     v-on:select="serviceDelaySelected">
                            </select2>
                            <b-form-invalid-feedback>{{ vp.errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                </b-form-row>

                <!-- Sitting Dates -->
                <div class="card">
                    <div class="card-header d-flex justify-content-between align-items-start">
                        <h5 class="card-title">Sitting Dates</h5>
                        <b-dropdown id="addCourtSittingButton" size="sm" variant="outline-secondary" split right v-on:click="addCourtSitting" v-if="!finalized && !cancelled">
                            <template v-slot:button-content>
                                <i class="fas fa-plus mr-1"></i>Add
                            </template>
                            <b-dropdown-item v-on:click="addCourtSitting">Single Sitting</b-dropdown-item>
                            <b-dropdown-item v-on:click="addCourtSittings">Multiple Sittings</b-dropdown-item>
                        </b-dropdown>
                    </div>

                    <b-table striped hover fixed small :items="quote.sittingDates" :fields="sittingDateFields" class="mb-0">
                        <template #cell(action)="row">
                            <b-button size="sm" v-if="!finalized && !cancelled" style="font-size: 10px;" @click="removeSittingDateFromList(row.index)" class="p1" variant="danger">
                                Remove
                            </b-button>
                        </template>
                    </b-table>

                    <validation-observer v-slot="vp" slim>
                        <b-modal :size="!addRange ? 'md' : 'lg'"
                                 centered
                                 no-close-on-backdrop
                                 no-close-on-esc
                                 v-model="addSittingsModalShown"
                                 v-on:ok.prevent="vp.handleSubmit(newSittingDate)">

                            <template #modal-header>
                                <h5 class="modal-title">New Sitting Date</h5>
                            </template>

                            <template #default>
                                <b-form-row>
                                    <validation-provider ref="multipleSittingDatesValidationProvider"
                                                         rules="required"
                                                         name="Sitting Date Range"
                                                         v-slot="{ valid, errors }"
                                                         slim
                                                         v-if="addRange">
                                        <b-form-group label="Sitting Date Range"
                                                      label-for="currentSittingDate_sittingDateRange"
                                                      class="col-md-6"
                                                      v-if="addRange">
                                            <ejs-daterangepicker name="currentSittingDate_sittingDateRange"
                                                                 v-model="currentSittingDate.sittingDateRange"
                                                                 format="dd/MM/yyyy"
                                                                 :renderDayCell="renderDayCell"
                                                                 @navigated="navigateCalendar"
                                                                 @open="openRangeCalendar">
                                            </ejs-daterangepicker>
                                            <b-form-invalid-feedback :state="errors[0] ? false : null">{{ errors[0] }}</b-form-invalid-feedback>
                                        </b-form-group>
                                    </validation-provider>
                                    <validation-provider ref="sittingDateValidationProvider"
                                                         rules="required"
                                                         name="Sitting Date"
                                                         v-slot="{ valid, errors }"
                                                         slim
                                                         v-if="!addRange">
                                        <b-form-group label="Sitting Date"
                                                      label-for="currentSittingDate_sittingDate"
                                                      class="col-md-4"
                                                      v-if="!addRange">
                                            <ejs-datepicker name="currentSittingDate_sittingDate"
                                                            v-model="currentSittingDate.sittingDate"
                                                            format="d/M/yyyy"
                                                            :renderDayCell="renderDayCell"
                                                            @navigated="navigateCalendar"
                                                            @open="openCalendar">
                                            </ejs-datepicker>
                                            <b-form-invalid-feedback :state="errors[0] ? false : null">{{ errors[0] }}</b-form-invalid-feedback>
                                        </b-form-group>
                                    </validation-provider>
                                    <b-form-group label="Start Time"
                                                  label-for="currentSittingDate_startTime"
                                                  :class="!addRange ? 'col-md-4' : 'col-md-3'">
                                        <b-form-input name="currentSittingDate_startTime"
                                                      type="time"
                                                      step="1"
                                                      v-model="currentSittingDate.startTime">
                                        </b-form-input>
                                    </b-form-group>
                                    <b-form-group label="End Time"
                                                  label-for="currentSittingDate_endTime"
                                                  :class="!addRange ? 'col-md-4' : 'col-md-3'">
                                        <b-form-input name="currentSittingDate_endTime"
                                                      type="time"
                                                      step="1"
                                                      v-model="currentSittingDate.endTime">
                                        </b-form-input>
                                    </b-form-group>
                                </b-form-row>
                                <b-form-group class="col-md-6">
                                    <b-form-checkbox name="excludeWeekends"
                                                     v-model="excludeWeekends"
                                                     v-if="addRange"
                                                     switch>
                                        Exclude Weekends and Public Holidays
                                    </b-form-checkbox>
                                </b-form-group>
                            </template>

                            <template #modal-footer="{ ok, cancel }">
                                <button type="button" class="btn btn-secondary" @click="cancel()">Cancel</button>
                                <button type="button" class="btn btn-primary" @click="ok()">Create</button>
                            </template>
                        </b-modal>
                    </validation-observer>

                    <div class="card-body">
                        <b-form-row>
                            <validation-provider ref="totalSittingDaysValidationProvider"
                                                 :rules="{ required:true, min_value:1 }"
                                                 name="Total Number of Days"
                                                 v-slot="{ validate, errors }"
                                                 class="col-md-2"
                                                 slim>
                                <b-form-group label="Total Number of Days"
                                              label-for="totalSittingDays">
                                    <b-form-input name="totalSittingDays"
                                                  type="number"
                                                  number
                                                  v-model="quote.totalSittingDays"
                                                  :disabled="finalized || cancelled"
                                                  v-on:change="totalSittingDaysChanged">
                                    </b-form-input>
                                </b-form-group>
                                <b-form-invalid-feedback :state="errors[0] ? false : null">{{ errors[0] }}</b-form-invalid-feedback>
                            </validation-provider>
                        </b-form-row>
                    </div>
                </div>

                <!-- Service Items -->
                <div class="card mt-2">
                    <div class="card-header d-flex justify-content-between align-items-start">
                        <b-row>
                            <b-button size="sm" class="mr-1" v-on:click="toggleAllServiceItemDetails" v-if="showClientSplitPercentages">
                                <i class="fas fa-minus" v-if="serviceItemDetailsExpanded"></i>
                                <i class="fas fa-plus" v-if="!serviceItemDetailsExpanded"></i>
                            </b-button>
                            <h5 class="card-title" split left>Service Items</h5>
                        </b-row>
                        <b-dropdown id="splitItemPercentagesButton" size="sm" variant="outline-secondary" split right v-on:click="addItem" :disabled="isUuidNullOrEmpty(quote.courtRegistryId)" v-if="!finalized && !cancelled">
                            <template v-slot:button-content>
                                <i class="fas fa-plus mr-1"></i>Add
                            </template>
                            <b-dropdown-item v-on:click="resetItemSplitPercentagesEqually">Split % Equally</b-dropdown-item>
                            <b-dropdown-item v-on:click="resetItemSplitPercentages">Reset Split % to Default</b-dropdown-item>
                        </b-dropdown>
                    </div>
                    <b-table striped hover fixed small :items="quote.serviceItems" :fields="serviceItemFields" foot-clone class="mb-0">
                        <template #cell(details)="row">
                            <b-button size="sm" @click="row.toggleDetails" class="m-1" v-if="showClientSplitPercentages">
                                <i class="fas fa-minus" v-if="row.detailsShowing"></i>
                                <i class="fas fa-plus" v-if="!row.detailsShowing"></i>
                            </b-button>
                        </template>
                        <template #cell(action)="row">
                            <b-button size="sm" variant="success" v-on:click="editItem(row.item)" v-if="!finalized && !cancelled">
                                Edit
                            </b-button>
                            <b-button size="sm" variant="danger" v-on:click="removeItem(row.item)" v-if="row.item.nonProductionChargeTypeId && !finalized && !cancelled">
                                Remove
                            </b-button>
                        </template>
                        <template #cell(quantityIsPerSittingDay)="row">
                            <b-form-checkbox v-model="row.item.quantityIsPerSittingDay" :disabled="true" switch>{{ row.item.quantityIsPerSittingDay ? 'Yes' : 'No' }}</b-form-checkbox>
                        </template>
                        <template #cell(chargeable)="row">
                            <b-form-checkbox v-model="row.item.chargeable" :disabled="true" switch>{{ row.item.chargeable ? 'Yes' : 'No' }}</b-form-checkbox>
                        </template>

                        <template #cell(courtQuoteLineItemSittingDayAssignmentCount)="row">
                            <b-form-group v-b-tooltip.hover :title="sittingDatePopup(row.item)">
                                <b-form-group v-model="row.item.courtQuoteLineItemSittingDayAssignmentCount" class="text-center">
                                    {{ row.item.courtQuoteLineItemSittingDayAssignmentCount }}
                                </b-form-group>
                            </b-form-group>
                        </template>

                        <template #row-details="row">
                            <b-form-row class="p-4">
                                <b-table striped hover fixed small :items="row.item.quoteLineItemClientShares" :fields="clientSharesFields" foot-clone class="mb-0">
                                    <template #foot(defaultSplitPercentage)>
                                        <span class="font-weight-bold" v-if="row.item.rounding != null && row.item.rounding != 0">Rounding</span>
                                        <br v-if="row.item.rounding != null && row.item.rounding != 0" />
                                        <span class="font-weight-bold">Total</span>
                                    </template>
                                    <template #foot(amount)>
                                        <span class="font-weight-bold" v-if="row.item.rounding != null && row.item.rounding != 0">{{ formatAsCurrency(row.item.rounding) }}</span>
                                        <br v-if="row.item.rounding != null && row.item.rounding != 0" />
                                        <span class="font-weight-bold">{{ formatAsCurrency(row.item.amount) }}</span>
                                    </template>
                                    <template #foot()>
                                        <span></span>
                                    </template>
                                </b-table>
                            </b-form-row>
                        </template>

                        <template #foot(chargeable)>
                            <span class="font-weight-bold" v-if="gstPercentage != 0">Subtotal</span>
                            <br v-if="gstPercentage != 0" />
                            <span class="font-weight-bold" v-if="gstPercentage != 0">{{ $t("Shared.Label.GST") }} ({{ gstPercentage }}%)</span>
                            <br v-if="gstPercentage != 0" />
                            <span class="font-weight-bold">Total</span>
                        </template>
                        <template #foot(amount)>
                            <span class="font-weight-bold" v-if="gstPercentage != 0">{{ formatAsCurrency(serviceItemsTotal) }}</span>
                            <br v-if="gstPercentage != 0" />
                            <span class="font-weight-bold" v-if="gstPercentage != 0">{{ formatAsCurrency(gstTotal) }}</span>
                            <br v-if="gstPercentage != 0" />
                            <span class="font-weight-bold">{{ formatAsCurrency(itemsTotal) }}</span>
                        </template>
                        <template #foot()>
                            <span></span>
                        </template>
                    </b-table>

                    <validation-observer v-slot="vp" slim>
                        <b-modal :size="showClientSplitPercentages? 'lg' : 'md'"
                                 centered
                                 :title="(currentServiceItem.originalItem == null) ? 'Add Service Item' : 'Edit Service Item'"
                                 ok-title="OK"
                                 cancel-title="Cancel"
                                 v-model="addOrEditItemModalShown"
                                 v-on:ok.prevent="vp.handleSubmit(createOrUpdateServiceItem)">

                            <b-form-row v-if="currentServiceItem.originalItem == null">
                                <validation-provider ref="serviceItemSelectValidationProvider" rules="required" name="Service Item" v-slot="{ valid, errors }" slim>
                                    <b-form-group label="Service Item"
                                                  label-for="serviceItemSelect"
                                                  :class="showClientSplitPercentages? 'col-md-8' : 'col-md-12'">
                                        <select2 ref="serviceItemSelect"
                                                 name="serviceItemSelect"
                                                 v-model="currentServiceItem.nonProductionChargeTypeId"
                                                 :options="nonProductionChargeTypeOptions"
                                                 key-property="nonProductionChargeTypeId"
                                                 text-property="name"
                                                 in-b-modal
                                                 tags
                                                 placeholder="Select or enter a service item"
                                                 v-on:select="nonProductionChargeTypeSelected"
                                                 :state="errors[0] ? false : null">
                                        </select2>
                                        <b-form-invalid-feedback :state="errors[0] ? false : null">{{ errors[0] }}</b-form-invalid-feedback>
                                    </b-form-group>
                                </validation-provider>
                            </b-form-row>
                            <b-form-row v-else>
                                <b-form-group label="Service Item"
                                              label-for="description"
                                              class="col-12">
                                    <b-form-input name="description"
                                                  v-model="currentServiceItem.description"
                                                  disabled>
                                    </b-form-input>
                                </b-form-group>
                            </b-form-row>
                            <b-form-row v-if="showNPPAllSittingDays">
                                <b-form-group class="col-md-6">
                                    <b-form-checkbox name="quantityIsPerSittingDay"
                                                     v-model="currentServiceItem.quantityIsPerSittingDay"
                                                     v-on:change="updateNPPSittingDaySelected"
                                                     switch>
                                        All Sitting Days
                                    </b-form-checkbox>
                                </b-form-group>
                            </b-form-row>
                            <b-form-row v-if="showNPPSittingDates">
                                <validation-provider ref="nppSittingDatesValidationProvider" 
                                                     name="Sitting Dates" 
                                                     :rules="{ required_if: (showNPPSittingDates = currentServiceItem.quantityIsPerSittingDay == false) }"
                                                     v-slot="{ valid, errors }" 
                                                     class="col-8"
                                                     slim>
                                    <b-form-group label="Sitting Dates"
                                                  label-for="sittingDaysSelect"
                                                  class="col-12">
                                        <select2 ref="sittingDaysSelect"
                                                 name="sittingDaysSelect"
                                                 v-model="currentServiceItem.sittingDates"
                                                 :options="nonProductionChargeSittingDayOptions"
                                                 key-property="courtQuoteSittingDayId"
                                                 text-property="sittingDateString"
                                                 in-b-modal
                                                 tags
                                                 multiple
                                                 allow-clear>
                                        </select2>
                                        <b-form-invalid-feedback :state="errors[0] ? false : null">{{ errors[0] }}</b-form-invalid-feedback>
                                    </b-form-group>
                                </validation-provider>
                            </b-form-row>
                            <b-form-row>
                                <validation-provider ref="quantityValidationProvider" rules="required" name="Quantity" v-slot="{ valid, errors }" slim class="col-8">
                                    <b-form-group :label="(currentServiceItem.unit == null) ? (currentServiceItem.quantityIsPerSittingDay == true) ? 'Quantity per Sitting Day' : 'Quantity' : (currentServiceItem.quantityIsPerSittingDay == true) ? 'Quantity (' + currentServiceItem.unit + ') per Sitting Day' : 'Quantity (' + currentServiceItem.unit + ')'"
                                                  label-for="quantity">
                                        <b-form-input name="quantity"
                                                      type="number"
                                                      number
                                                      v-model="currentServiceItem.quantity"
                                                      :state="errors[0] ? false : null">
                                        </b-form-input>
                                        <b-form-invalid-feedback :state="errors[0] ? false : null">{{ errors[0] }}</b-form-invalid-feedback>
                                    </b-form-group>
                                </validation-provider>
                                <validation-provider ref="unitCostValidationProvider" rules="required" name="Rate" v-slot="{ valid, errors }" slim class="col-4">
                                    <b-form-group label="Rate"
                                                  label-for="unitCost">
                                        <currency-input name="unitCost"
                                                        :currency="$config.defaultCurrencyCode"
                                                        v-model="currentServiceItem.unitCost"
                                                        :class="errors[0] ? 'text-right form-control is-invalid' : 'text-right form-control'">
                                        </currency-input>
                                        <b-form-invalid-feedback :state="errors[0] ? false : null">{{ errors[0] }}</b-form-invalid-feedback>
                                    </b-form-group>
                                </validation-provider>
                            </b-form-row>
                            <b-form-row>
                                <b-form-group class="col-md-6">
                                    <b-form-checkbox name="chargeable"
                                                     v-model="currentServiceItem.chargeable"
                                                     switch>
                                        Chargeable
                                    </b-form-checkbox>
                                </b-form-group>
                            </b-form-row>

                            <b-form-row v-if="!showClientSplitPercentages && currentServiceItem.splitCosts">
                                <div class="col-md-6">
                                    <b-form-checkbox name="splitCosts"
                                                     v-model="currentServiceItem.splitCosts"
                                                     switch>
                                        Split Cost
                                    </b-form-checkbox>
                                </div>
                            </b-form-row>
                            <b-form-row v-if="!showClientSplitPercentages">
                                <validation-provider ref="percentageValidationProvider"
                                                     v-if="currentServiceItem.splitCosts"
                                                     :rules="{ min_value:1, max_value:99, required_if: currentServiceItem.splitCosts }"
                                                     name="Percentage"
                                                     v-slot="{ validate, errors }"
                                                     class="col-md-6">
                                    <b-form-group label="Split %"
                                                  label-for="percentage">
                                        <b-form-input name="percentage"
                                                      type="number"
                                                      number
                                                      :min="1"
                                                      :max="99"
                                                      v-model="currentServiceItem.splitPercentage"
                                                      :disabled="!currentServiceItem.splitCosts"
                                                      :state="errors[0] ? false : null">
                                        </b-form-input>
                                        <b-form-invalid-feedback :state="errors[0] ? false : null">{{ errors[0] }}</b-form-invalid-feedback>
                                    </b-form-group>
                                </validation-provider>
                            </b-form-row>

                            <b-form-row v-if="showClientSplitPercentages" class="d-flex flex-column align-items-end">
                                <b-dropdown id="addClientButton" size="sm" variant="outline-secondary" split right v-on:click="resetItemClientSplitPercentagesEqually">
                                    <template v-slot:button-content>
                                        Split % Equally
                                    </template>
                                    <b-dropdown-item v-on:click="resetItemClientSplitPercentages">Reset Split % to Default</b-dropdown-item>
                                </b-dropdown>
                                <b-table striped hover fixed small :items="currentServiceItem.quoteLineItemClientShares" :fields="clientLineItemSplitPercentageFields" class="mb-0">
                                    <template #cell(splitPercentage)="row">
                                        <b-form-input type="number" number v-model="row.item.defaultSplitPercentage" switch>{{ row.item.defaultSplitPercentage }}</b-form-input>
                                    </template>
                                </b-table>
                            </b-form-row>
                        </b-modal>
                    </validation-observer>
                </div>

                <!-- Is Estimate -->
                <b-form-row class="mt-3">
                    <b-form-group v-b-tooltip.hover title="This selection will determine labels used in generated documents (Estimate or Quote)">
                        <b-form-radio-group id="isEstimate"
                                            v-model="quote.isEstimate"
                                            name="isEstimate">
                            <b-form-radio :disabled="finalized || cancelled" name="isEstimate" :value="true">Estimate</b-form-radio>
                            <b-form-radio :disabled="finalized || cancelled" name="isEstimate" :value="false">Quote</b-form-radio>
                        </b-form-radio-group>
                    </b-form-group>
                </b-form-row>

                <!-- Validity -->
                <b-form-row class="justify-content-between">
                    <b-form-group label="Validity Period (days)"
                                  label-for="validityDays"
                                  class="col-md-2">
                        <b-form-input name="validityDays"
                                      type="number"
                                      number
                                      v-model="quote.validityDays"
                                      :disabled="finalized || cancelled">
                        </b-form-input>
                    </b-form-group>
                </b-form-row>

                <!--Comments-->
                <comments :api-controller-url="apiControllerUrl('comment')"
                          :current-user-account-id="currentUserAccountId"
                          :quote-id="quote.quoteId"
                          v-model="newQuoteComments"
                          :can-create-comment="canCreateComment"
                          :can-edit-and-retract-all-comments="canEditAndRetractAllComments"
                          :disabled="!allowAddComments"
                          :default-comment-type="$enum.CommentType.Quote"
                          class="my-2">
                </comments>

                <!--Cancel Modal-->
                <validation-observer v-slot="vp" slim>
                    <b-modal size="md"
                             centered
                             :title="(currentServiceItem.isEstimate) ? 'Cancel Estimate' : 'Cancel Quote'"
                             ok-title="Yes, Cancel Quote"
                             cancel-title="Do Not Cancel"
                             v-model="cancelQuoteModalShown"
                             v-on:ok.prevent="vp.handleSubmit(cancelQuote)">

                        <b-form-row>
                            <validation-provider ref="cancellationReasonSelectValidationProvider" rules="required" name="Cancellation Reason" v-slot="{ valid, errors }" slim>
                                <b-form-group label="Cancellation Reason"
                                              label-for="cancellationReasonSelect"
                                              class="col-12">
                                    <select2 ref="cancellationReasonSelect"
                                             name="cancellationReasonSelect"
                                             v-model="quote.quoteCancelledReason"
                                             :options="allQuoteCancelledReasonOptions"
                                             in-b-modal
                                             placeholder="Select a cancellation reason"
                                             :state="errors[0] ? false : null">
                                    </select2>
                                    <b-form-invalid-feedback :state="errors[0] ? false : null">{{ errors[0] }}</b-form-invalid-feedback>
                                </b-form-group>
                            </validation-provider>
                        </b-form-row>
                    </b-modal>
                </validation-observer>

                <validation-summary ref="validationSummary" :vee-validate-observer-errors="vo.errors"></validation-summary>

                <div class="btn-toolbar justify-content-end mt-2" role="toolbar" aria-label="Action buttons">
                    <div class="btn-group ml-2" role="group">
                        <b-button variant="outline-primary" v-on:click="printQuote" v-if="finalized && (quote.clients == null || quote.clients.length <= 1)"><i class="far fa-file-pdf mr-1"></i>Download PDF</b-button>
                        <b-button variant="outline-success" v-on:click.prevent="trySaveQuote('save')" v-if="!finalized && !cancelled"><i class="fas fa-check mr-1"></i>Save</b-button>
                        <b-button variant="outline-success" v-on:click.prevent="trySaveQuote('close')" v-if="!finalized && !cancelled"><i class="fas fa-check mr-1"></i>Save and Close</b-button>
                        <b-button variant="outline-success" v-on:click.prevent="trySaveQuote('new')" v-if="!finalized && !cancelled"><i class="fas fa-check mr-1"></i>Save and Create New</b-button>
                        <b-button variant="outline-success" v-on:click.prevent="finalizeQuote" v-if="!finalized && !cancelled"><i class="fas fa-check mr-1"></i>Finalise Quote</b-button>
                        <b-button variant="outline-danger" v-on:click.prevent="showCancelQuote" v-if="quote.quoteId != null && quote.orderId == null && !cancelled"><i class="fas fa-times mr-1"></i>Cancel Quote</b-button>
                        <b-button variant="outline-secondary" v-on:click="closeAndCancel"><i class="fas fa-arrow-left mr-1"></i>Close</b-button>
                    </div>
                </div>
            </validation-observer>
            <b-input-group size="sm" prepend="Created at" class="mt-2" v-if="!isUuidNullOrEmpty(quote.quoteId)">
                <b-form-input :value="quote.createdDateTimeUTC | formatDateTime('DD/MM/YYYY h:mm A')" readonly></b-form-input>
                <b-input-group-prepend is-text class="input-group-append">By</b-input-group-prepend>
                <b-form-input :value="(quote.createdByUserAccount != null) ? quote.createdByUserAccount.displayName : null" readonly></b-form-input>
                <b-input-group-prepend is-text class="input-group-append">Last update at</b-input-group-prepend>
                <b-form-input :value="quote.lastUpdateDateTimeUTC | formatDateTime('DD/MM/YYYY h:mm A')" readonly></b-form-input>
                <b-input-group-prepend is-text class="input-group-append">By</b-input-group-prepend>
                <b-form-input :value="(quote.lastUpdateByUserAccount != null) ? quote.lastUpdateByUserAccount.displayName : null" readonly></b-form-input>
            </b-input-group>
        </b-overlay>
    </div>
</template>

<script>
    import $ from 'jquery';
    import Vue from 'vue';
    import { BootstrapVue, BootstrapVueIcons } from 'bootstrap-vue';
    import axios from 'axios';
    import ErrorsMixin from '../../mixins/ErrorsMixin';
    import DateTimeMixin from '../../mixins/DateTimeMixin';
    import UuidMixins from '../../mixins/UuidMixins';
    import NumbersMixin from '../../mixins/NumbersMixin';
    import VueCurrencyInput from 'vue-currency-input'
    import { DateTimePickerPlugin } from '@syncfusion/ej2-vue-calendars';
    import { ValidationProvider, ValidationObserver } from 'vee-validate';
    import { extend, localize } from 'vee-validate';
    import { required, required_if, min_value, max_value } from 'vee-validate/dist/rules';
    import en from 'vee-validate/dist/locale/en.json';
    //import { addClass } from "@syncfusion/ej2-base";
    extend('required', required);
    extend('required_if', required_if);
    extend('min_value', min_value);
    extend('max_value', max_value);
    localize('en', en);

    import Select2 from '../Select2';
    import ValidationSummary from '../ValidationSummary.vue';

    Vue.use(DateTimePickerPlugin);
    Vue.component('select2', Select2);
    Vue.component('validation-provider', ValidationProvider);
    Vue.component('validation-observer', ValidationObserver);
    Vue.component('validation-summary', ValidationSummary);
    Vue.use(BootstrapVue);
    Vue.use(BootstrapVueIcons);
    Vue.use(VueCurrencyInput);

    export default {
        name: "CourtQuoteDetails",
        mixins: [ErrorsMixin, DateTimeMixin, UuidMixins, NumbersMixin],
        props: {
            initialQuote: {
                type: Object,
                default: null
            },
            initialClientOptions: {
                type: Array,
                default: null
            },
            initialClientManagesAssignedContracts: {
                type: Boolean,
                default: false
            },
            initialClientBranches: {
                type: Array,
                default: null
            },
            initialClientContacts: {
                type: Array,
                default: null
            },
            initialCourtRegistries: {
                type: Array,
                default: null
            },
            initialCourtJurisdictions: {
                type: Array,
                default: null
            },
            initialServiceTypes: {
                type: Array,
                default: null
            },
            initialServiceDelays: {
                type: Array,
                default: null
            },
            initialNonProductionChargeTypes: {
                type: Array,
                default: null
            },
            initialServiceTypeRequireServiceDelay: {
                type: Boolean,
                default: false
            },
            initialQuoteClientsToCopy: {
                type: Array,
                default: null
            },
            allQuoteCancelledReasonOptions: {
                type: Array,
                default: null
            },
            currentUserAccountId: {
                type: String,
                required: true
            },
            canCreateComment: {
                type: Boolean,
                default: false
            },
            canEditAndRetractAllComments: {
                type: Boolean,
                default: false
            },
        },
        data() {
            return {
                showSpinner: false,
                gstPercentage: this.$config.payment.defaultGstPercentage,
                addRange: false,
                newSittingsDateRange: null,
                currentSittingDate: {
                    courtQuoteSittingDayId: null,
                    sittingDate: null,
                    sittingDateRange: null,
                    startTime: null,
                    endTime: null,
                    courtRoomId: null,
                    courtRoom: null,
                    courtSittingMatterTypeId: null,
                    courtSittingMatterType: null,
                },
                sittingDateFields: [
                    {
                        key: 'sittingDate',
                        label: 'Sitting Date',
                        class: 'text-left ',
                        formatter: (value) => {
                            return value ? this.formatDateTime(value, 'dddd DD/MM/YYYY', true, true) : value;
                        }
                    },
                    {
                        key: 'startTime',
                        label: 'Start Time',
                        class: 'text-left ',
                        formatter: (value) => {
                            return value ? this.formatDateTime(value, 'h:mm:ss A', true, true) : value;
                        }
                    },
                    {
                        key: 'endTime',
                        label: 'End Time',
                        class: 'text-left ',
                        formatter: (value) => {
                            return value ? this.formatDateTime(value, 'h:mm:ss A', true, true) : value;
                        }
                    },
                    {
                        key: 'action',
                        label: '',
                        class: 'text-right'
                    }
                ],
                addSittingsModalShown: false,
                serviceItemsTotal: null,
                gstTotal: null,
                itemsTotal: null,
                serviceItemFields: [
                    {
                        key: 'details',
                        label: '',
                        thStyle: {
                            width: '3rem'
                        }
                    },
                    {
                        key: 'description',
                        label: 'Description',
                        class: 'text-left'
                    },
                    {
                        key: 'subDescription',
                        label: "",
                        class: 'text-left'
                    },
                    {
                        key: 'quantity',
                        label: 'Quantity',
                        class: 'text-right',
                        formatter: (value, key, item) => {
                            return value ? item.unit ? value + ' ' + item.unit : value : value;
                        },
                        thStyle: {
                            width: '8rem'
                        }
                    },
                    {
                        key: 'quantityIsPerSittingDay',
                        label: 'Per Day',
                        class: 'text-right'
                    },
                    {
                        key: 'courtQuoteLineItemSittingDayAssignmentCount',
                        label: 'Sitting Dates',
                        class: 'text-right',
                        //formatter: (value) => {
                        //    return value > 0 ? value : null;
                        //},
                    },
                    {
                        key: 'unitCost',
                        label: 'Rate',
                        class: 'text-right',
                        formatter: (value) => {
                            return value ? this.formatAsCurrency(value) : value;
                        },
                        thStyle: {
                            width: '8rem'
                        }
                    },
                    {
                        key: 'chargeable',
                        label: 'Chargeable',
                        class: 'text-right'
                    },
                    {
                        key: 'amount',
                        label: 'Amount',
                        class: 'text-right',
                        formatter: (value) => {
                            return value ? this.formatAsCurrency(value) : value;
                        },
                        thStyle: {
                            width: '8rem'
                        }
                    },
                    {
                        key: 'splitPercentage',
                        label: 'Split %',
                        class: 'text-right',
                        thStyle: {
                            width: '8rem'
                        }
                    },
                    {
                        key: 'action',
                        label: '',
                        class: 'text-right',
                        thStyle: {
                            width: '10rem'
                        }
                    }
                ],
                clientFields: [
                    {
                        key: 'clientName',
                        label: 'Ordering Client',
                        class: 'text-left'
                    },
                    {
                        key: 'clientBranchName',
                        label: "Ordering Client Branch",
                        class: 'text-left'
                    },
                    {
                        key: 'clientContactName',
                        label: "Ordering Client Contact",
                        class: 'text-left'
                    },
                    {
                        key: 'defaultSplitPercentage',
                        label: "Default Split %",
                        class: 'text-right',
                        thStyle: {
                            width: '8rem'
                        }
                    },
                    {
                        key: 'action',
                        label: '',
                        class: 'text-right',
                        thStyle: {
                            width: '10rem'
                        }
                    }
                ],
                clientLineItemSplitPercentageFields: [
                    {
                        key: 'clientName',
                        label: 'Client',
                        class: 'text-left'
                    },
                    {
                        key: 'clientBranchName',
                        label: "Branch",
                        class: 'text-left'
                    },
                    {
                        key: 'clientContactName',
                        label: "Contact",
                        class: 'text-left'
                    },
                    {
                        key: 'splitPercentage',
                        label: "Split %",
                        class: 'text-left',
                        thStyle: {
                            width: '8rem'
                        }
                    },
                ],
                clientSharesFields: [
                    {
                        key: 'clientName',
                        label: 'Client',
                        class: 'text-left'
                    },
                    {
                        key: 'clientBranchName',
                        label: "Branch",
                        class: 'text-left'
                    },
                    {
                        key: 'clientContactName',
                        label: "Contact",
                        class: 'text-left'
                    },
                    {
                        key: 'defaultSplitPercentage',
                        label: "Split %",
                        class: 'text-right',
                        thStyle: {
                            width: '8rem'
                        }
                    },
                    {
                        key: 'amount',
                        label: 'Amount',
                        class: 'text-right',
                        formatter: (value) => {
                            return value ? this.formatAsCurrency(value) : value;
                        },
                        thStyle: {
                            width: '8rem'
                        }
                    },
                ],
                addOrEditItemModalShown: false,
                addOrEditClientModalShown: false,
                cancelQuoteModalShown: false,
                currentServiceItem: {
                    originalItem: null,
                    quoteLineItemId: null,
                    lineItemNumber: null,
                    lineItemType: null,
                    rateType: null,
                    nonProductionChargeTypeId: null,
                    itemType: null,
                    name: null,
                    description: null,
                    subDescription: null,
                    quantity: null,
                    unit: null,
                    unitCost: null,
                    splitCosts: false,
                    splitPercentage: 50,
                    chargeable: true,
                    quantityIsPerSittingDay: false,
                    sittingDates: [{
                        quoteLineItemId: null,
                        courtQuoteSittingDayId: null,
                        sittingDateString: null,
                    }],
                    quoteLineItemClientShares: [{
                        quoteLineItemClientShareId: null,
                        clientId: null,
                        clientName: null,
                        clientBranchId: null,
                        clientBranchName: null,
                        clientContactId: null,
                        clientContactName: null,
                        splitPercentage: null,
                    }],
                    rounding: 0,
                },

                //new data
                quote: {
                    quoteStatus: this.initialQuote ? this.initialQuote.quoteStatusDisplayName : "In Progress",
                    quoteId: this.initialQuote && !this.isUuidNullOrEmpty(this.initialQuote.quoteId) ? this.initialQuote.quoteId : null,
                    quoteNumberDisplayText: this.initialQuote ? this.initialQuote.quoteNumberDisplayText : null,
                    clientId: this.initialQuote ? this.initialQuote.clientId : null,
                    clients: this.initialQuoteClientsToCopy ? this.initialQuoteClientsToCopy : [],
                    clientBranchId: this.initialQuote ? this.initialQuote.clientBranchId : null,
                    clientContactId: this.initialQuote ? this.initialQuote.clientContactId : null,
                    courtRegistryId: this.initialQuote ? this.initialQuote.courtRegistryId : null,
                    courtJurisdictionId: this.initialQuote ? this.initialQuote.courtJurisdictionId : null,
                    location: this.initialQuote ? this.initialQuote.location : null,
                    matterNumber: this.initialQuote ? this.initialQuote.matterNumber : null,
                    matterName: this.initialQuote ? this.initialQuote.matterName : null,
                    serviceTypeId: this.initialQuote ? this.initialQuote.serviceTypeId : null,
                    serviceDelayId: this.initialQuote ? this.initialQuote.serviceDelayId : null,
                    sittingDates: this.initialQuote ? this.initialQuote.sittingDays : [],
                    serviceItems: this.initialQuote ? this.initialQuote.lineItems : [],
                    validityDays: this.initialQuote ? this.initialQuote.validityPeriodDays : 30,
                    isEstimate: this.initialQuote ? this.initialQuote.isEstimate : true,
                    totalSittingDays: this.initialQuote ? this.initialQuote.totalSittingDays : 0,
                    finalizedDateTimeUTC: this.initialQuote ? this.initialQuote.finalizedDateTimeUTC : null,
                    quoteFileId: this.initialQuote ? this.initialQuote.quoteFileId : null,
                    createdByUserAccount: this.initialQuote ? this.initialQuote.createdByUserAccount : null,
                    createdDateTimeUTC: this.initialQuote ? this.initialQuote.createdDateTimeUTC : null,
                    lastUpdateByUserAccount: this.initialQuote ? this.initialQuote.lastUpdateByUserAccount : null,
                    lastUpdateDateTimeUTC: this.initialQuote ? this.initialQuote.lastUpdateDateTimeUTC : null,
                    disabledDateTimeUTC: this.initialQuote ? this.initialQuote.disabledDateTimeUTC : null,
                    disabledByUserAccount: this.initialQuote ? this.initialQuote.disabledByUserAccount : null,
                    disabledByUserAccountDisplayName: this.initialQuote ? this.initialQuote.disabledByUserAccountDisplayName : null,
                    orderId: this.initialQuote ? this.initialQuote.orderId : null,
                    quoteCancelledReason: this.initialQuote ? this.initialQuote.quoteCancelledReason : null,
                    quoteCancelledReasonDisplayName: this.initialQuote ? this.initialQuote.quoteCancelledReasonDisplayName : null,
                    defaultSplitPercentage: this.initialQuote ? this.initialQuote.defaultSplitPercentage : null,
                },

                currentClient: {
                    quoteClientId: null,
                    clientId: null,
                    clientName: null,
                    clientBranchId: null,
                    clientBranchName: null,
                    clientContactId: null,
                    clientContactName: null,
                    defaultSplitPercentage: null,
                    quoteFileId: null,
                },

                clientOptions: this.initialClientOptions,
                clientBranchOptions: this.initialBranchOptions,
                clientContactOptions: this.initialContactOptions,
                clientManagesAssignedContracts: this.initialClientManagesAssignedContracts,
                courtJuristictionOptions: this.initialCourtJurisdictions,
                serviceTypeOptions: this.initialServiceTypes,
                serviceDelayOptions: this.initialServiceDelays,
                nonProductionChargeTypeOptions: this.initialNonProductionChargeTypes,
                selectedServiceTypeRequiresServiceDelay: this.initialServiceTypeRequireServiceDelay,
                excludeWeekends: true,
                publicHolidayList: [],
                publicHolidayYearsLoaded: [],
                calendarCSSClass: "datepicker-special-dates",
                newQuoteComments: null,
                splitPercentageRounding: 1.0,
                serviceItemDetailsExpanded: true,

                showNPPAllSittingDays: true,
                showNPPSittingDates: true,
            };
        },
        computed: {
            finalized: function () {
                if (this.quote == null) return false;
                return this.quote.finalizedDateTimeUTC != null;
            },
            cancelled: function () {
                if (this.quote == null) return false;
                return this.quote.disabledDateTimeUTC != null;
            },
            allowSplit: function () {
                if (this.finalized) return false;
                if (this.quote.clientId) return false;
                if (this.quote.clients == null) return false;
                return this.quote.clients.length > 1;
            },
            allowAddComments: function () {
                if (this.quote == null) {
                    return true;
                }
                else if (this.quote?.orderId == null && this.quote?.disabledDateTimeUTC == null) {
                    return true;
                }
                else {
                    return false;
                }
            },
            nonProductionChargeSittingDayOptions: function () {
                var dayList = [];

                this.quote.sittingDates.forEach(day => {
                    //if (day.courtQuoteSittingDayId != null) {
                    dayList.push({
                        //disabled: false,
                        //group: null,
                        //selected: false,
                        //text: this.formatSittingDateTimeAsString(day.sittingDate, day.startTime, day.endTime),
                        //value: day.courtQuoteSittingDayId,
                        courtQuoteSittingDayId: day.courtQuoteSittingDayId,
                        sittingDateString: this.formatSittingDateTimeAsString(day.sittingDate, day.startTime, day.endTime)
                    });
                    //}
                });

                return dayList;
            },
            maxDefaultSplitPercentage: function () {
                if (this.quote.clients == null || this.quote.clients.length == 0) { return 100; }
                else {
                    var maxSplit = 100;
                    this.quote.clients.forEach((client) => {
                        if (client.defaultSplitPercentage != null && client.quoteClientId != this.currentClient.quoteClientId) {
                            maxSplit -= client.defaultSplitPercentage;
                        }
                    });
                    if (maxSplit > 0) { return maxSplit; }
                    else { return 0; }
                }

            },
            showClientSplitPercentages: function () {
                if (this.quote?.clients?.length > 1) { return true; }
                else { return false; }
            },
            hasSplitItemCostErrors: function () {
                if (this.showClientSplitPercentages) {
                    var minSplitPercentage = (100 - parseFloat(this.splitPercentageRounding));
                    var splitError = false;

                    this.quote.serviceItems?.forEach(item => {
                        var totalSplitPercentage = 0;

                        item.quoteLineItemClientShares.forEach(client => {
                            totalSplitPercentage += client.defaultSplitPercentage;
                        });

                        if ((totalSplitPercentage < minSplitPercentage) || totalSplitPercentage > 100) {
                            this.showErrorsToast(["Total Split % must be between " + minSplitPercentage + "% and 100% for Service Item '" + item.description + "'."]);
                            splitError = true;
                            return true;
                        }
                    });
                    if (splitError) { return true; }
                    else { return false; }
                }
                return false;
            },
        },
        methods: {
            formatSittingDateTimeAsString: function (sittingDate, startTime, endTime) {
                if (startTime != null && endTime != null) {
                    return this.formatDateTime(sittingDate, 'DD/MM/YYYY', true, true) + ' '
                        + this.formatDateTime(startTime, 'h:mm:ss A', true, true) + ' - '
                        + this.formatDateTime(endTime, 'h:mm:ss A', true, true);
                }
                else if (startTime != null && endTime == null) {
                    return this.formatDateTime(sittingDate, 'DD/MM/YYYY', true, true) + 'Starts: ' + this.formatDateTime(startTime, 'h:mm:ss A', true, true);
                }
                else if (startTime == null && endTime != null) {
                    return this.formatDateTime(sittingDate, 'DD/MM/YYYY', true, true) + 'Ends: ' + this.formatDateTime(endTime, 'h:mm:ss A', true, true);
                }

                return this.formatDateTime(sittingDate, 'DD/MM/YYYY', true, true);
            },
            sittingDayAssignmentsString: function (sittingDates) {
                var retval = '';
                sittingDates.forEach(sittingDate => {
                    retval += sittingDate.sittingDateString + ', ';
                });
                retval = retval.slice(0, -2);
                return retval;
            },

            addCourtSitting: function () {
                this.addRange = false;
                this.showModal();
            },
            addCourtSittings: function () {
                this.addRange = true;
                this.showModal();
            },
            resetDateValidationProviders: function (typeOfDateSelection) {
                if (typeOfDateSelection == 'single') this.$refs.sittingDateValidationProvider.reset();
                if (typeOfDateSelection == 'multiple') this.$refs.multipleSittingDatesValidationProvider.reset();
            },
            showModal: function () {
                this.newSittingsDateRange = null;
                this.currentSittingDate.courtQuoteSittingDayId = null;
                this.currentSittingDate.sittingDate = null;
                this.currentSittingDate.startSittingDate = null;
                this.currentSittingDate.endSittingDate = null;
                this.currentSittingDate.startTime = null;
                this.currentSittingDate.endTime = null;
                this.currentSittingDate.courtRoomId = null;
                this.currentSittingDate.courtRoom = null;
                this.currentSittingDate.courtSittingMatterTypeId = null;
                this.currentSittingDate.courtSittingMatterType = null;
                this.addSittingsModalShown = true;

                if (this.addRange) this.loadPublicHolidaysForRangeCalendar();
                else this.loadPublicHolidaysForCalendar();

                Vue.nextTick().then(() => {
                    if (this.addRange) {
                        this.$refs.multipleSittingDatesValidationProvider.reset();
                    }
                    else {
                        this.$refs.sittingDateValidationProvider.reset();
                    }
                });
            },
            newSittingDate: function () {
                var newSittingDateObject = {};
                Object.assign(newSittingDateObject, this.currentSittingDate);
                if (this.addRange == true) {
                    var startDate = newSittingDateObject.sittingDateRange[0];
                    var endDate = newSittingDateObject.sittingDateRange[1];
                    var currentDate = startDate;
                    var allDates = [];
                    do {
                        if (!this.excludeWeekends) {
                            this.addSittingDate(currentDate);
                        }
                        else {
                            //check if weekend
                            if (this.getDayOfWeek(currentDate) <= 5) {
                                allDates.push(this.formatDateTime(currentDate, "yyyy-MM-DD", true, false));
                            }
                        }
                        currentDate = this.addDays(currentDate, 1);
                    } while (currentDate <= endDate);
                    if (this.excludeWeekends) {
                        var url = this.$urlHelper.getApiUrl('PublicHoliday', 'excludepublicholidays');
                        var data = {
                            dates: allDates,
                            courtRegistryId: this.quote.courtRegistryId,
                            interviewOrganisationId: null,
                        };

                        axios
                            .post(url, JSON.stringify(data),
                                {
                                    headers: {
                                        'Content-Type': 'application/json'
                                    }
                                })
                            .then(response => {
                                var allDatesExcludingPublicHolidays = response.data;
                                allDatesExcludingPublicHolidays.forEach(date => {
                                    this.addSittingDate(date);
                                });
                                this.addSittingsModalShown = false;
                                this.quote.totalSittingDays = this.quote.sittingDates.length;
                                this.updateServiceItemQuantities();
                            })
                            .catch(ex => {
                                var currentErrors = this.handleAxiosException(ex);
                                this.showErrorsToast(currentErrors);
                            });
                    } else {
                        this.addSittingsModalShown = false;
                        this.quote.totalSittingDays = this.quote.sittingDates.length;
                        this.updateServiceItemQuantities();
                    }
                }
                else {
                    this.addSittingDate(newSittingDateObject.sittingDate);
                    this.addSittingsModalShown = false;
                    this.quote.totalSittingDays = this.quote.sittingDates.length;
                    this.updateServiceItemQuantities();
                }
            },
            addSittingDate: function (sittingDate) {
                var newSittingDateObject = {};
                Object.assign(newSittingDateObject, this.currentSittingDate);
                if (sittingDate) {
                    //the input from the UI control has a time of 00:00:00 but the local timezone, to get rid of the timezone we have to
                    //convert the date to a string first and then create our own date/time object from it
                    newSittingDateObject.sittingDate = this.parseDateTime(this.formatDateTime(sittingDate, 'DD/MM/YYYY'), 'DD/MM/YYYY');
                }
                if (newSittingDateObject.startTime != null) {
                    newSittingDateObject.startTime = this.parseDateTime(newSittingDateObject.startTime, 'HH:mm:ss', true, true);
                }
                if (newSittingDateObject.endTime != null) {
                    newSittingDateObject.endTime = this.parseDateTime(newSittingDateObject.endTime, 'HH:mm:ss', true, true);
                }
                newSittingDateObject.courtQuoteSittingDayId = this.newUuid();
                newSittingDateObject.new = true;
                if (this.quote.sittingDates == null) this.quote.sittingDates = [];
                this.quote.sittingDates.push(newSittingDateObject);
            },
            removeSittingDateFromList: function (index) {
                var sittingDateToDelete = this.quote.sittingDates[index];
                //console.log('courtQuoteSittingDayId: ' + sittingDateToDelete.courtQuoteSittingDayId);

                //remove the date from any service items
                this.quote.serviceItems?.forEach(serviceItem => {
                    if (serviceItem.courtQuoteLineItemSittingDayAssignments != null) {
                        var dateIndex = serviceItem.courtQuoteLineItemSittingDayAssignments.findIndex(e =>
                            e.courtQuoteSittingDayId == sittingDateToDelete.courtQuoteSittingDayId);

                        //console.log('dateIndex: ' + dateIndex);
                        if (dateIndex != null && dateIndex >= 0) {
                            serviceItem.courtQuoteLineItemSittingDayAssignments.splice(dateIndex, 1);

                            if (serviceItem.courtQuoteLineItemSittingDayAssignments.length == 0) {
                                serviceItem.courtQuoteLineItemSittingDayAssignments = [];
                            }
                        }
                    }
                });

                //remove the date
                this.quote.sittingDates.splice(index, 1);
                this.quote.totalSittingDays = this.quote.totalSittingDays - 1;

                this.quote.totalSittingDays = this.quote.sittingDates.length;
                this.updateServiceItemQuantities();
                //console.log('Removed');
            },
            totalSittingDaysChanged: function () {
                if (this.quote.totalSittingDays < this.quote.sittingDates.length) { this.quote.totalSittingDays = this.quote.sittingDates.length; }
                this.updateServiceItemQuantities();
            },
            updateServiceItemQuantities: function () {
                var totalAudioMinutes = 0;
                if (this.quote.totalSittingDays > 0) {
                    this.quote.sittingDates.forEach(sittingDate => {
                        if ((sittingDate.startTime != null) && (sittingDate.endTime != null)) {
                            var audioMinutes = this.getDifferenceInMinutes(sittingDate.startTime, sittingDate.endTime);
                            if (audioMinutes > 0) totalAudioMinutes += audioMinutes;
                        } else {
                            totalAudioMinutes += 4.5 * 60;
                        }
                    });
                    if (this.quote.totalSittingDays > this.quote.sittingDates.length) {
                        totalAudioMinutes += 4.5 * 60 * (this.quote.totalSittingDays - this.quote.sittingDates.length);
                    }
                }
                if (totalAudioMinutes > 0) {
                    this.quote.serviceItems.forEach(serviceItem => {
                        if (serviceItem.minutesToQuantityRatio) {
                            serviceItem.quantity = Math.ceil(totalAudioMinutes / serviceItem.minutesToQuantityRatio);
                        } else if (serviceItem.daysToQuantityRatio) {
                            serviceItem.quantity = Math.ceil(this.quote.totalSittingDays / serviceItem.daysToQuantityRatio);
                        }
                    });
                    this.calculateTotals();
                }
            },
            addItem: function () {
                this.currentServiceItem.originalItem = null;
                this.currentServiceItem.quoteLineItemId = null;
                this.currentServiceItem.lineItemType = null;
                this.currentServiceItem.lineItemNumber = null;
                this.currentServiceItem.rateType = null;
                this.currentServiceItem.nonProductionChargeTypeId = null;
                this.currentServiceItem.itemType = null;
                this.currentServiceItem.name = null;
                this.currentServiceItem.description = null;
                this.currentServiceItem.subDescription = null;
                this.currentServiceItem.quantity = null;
                this.currentServiceItem.unit = null;
                this.currentServiceItem.unitCost = null;
                this.currentServiceItem.splitCosts = false;
                this.currentServiceItem.splitPercentage = null;
                this.currentServiceItem.chargeable = true;
                this.currentServiceItem.quantityIsPerSittingDay = false;
                this.currentServiceItem.sittingDates = [];
                this.currentServiceItem.courtQuoteLineItemSittingDayAssignmentCount = null;
                this.currentServiceItem.courtQuoteLineItemSittingDayAssignmentString = null;
                this.currentServiceItem.quoteLineItemClientShares = [];

                if (this.showClientSplitPercentages) {
                    this.quote.clients.forEach(client => {
                        if (this.isUuidNullOrEmpty(client.quoteClientId)) client.quoteClientId = this.newUuid();

                        this.currentServiceItem.quoteLineItemClientShares.push({
                            quoteLineItemClientShareId: null,
                            quoteClientId: client.quoteClientId,
                            clientName: client.clientName,
                            clientBranchName: client.clientBranchName,
                            clientContactName: client.clientContactName,
                            defaultSplitPercentage: client.defaultSplitPercentage,
                            amount: 0,
                            rounding: 0,
                        });
                    });

                    var count = 0;
                    this.currentServiceItem.quoteLineItemClientShares.forEach(client => {
                        if (client.defaultSplitPercentage == null) count++;
                    });

                    if (count != null) {
                        var splitPerClient = parseFloat((this.maxDefaultSplitPercentage / count).toFixed(2));

                        this.currentServiceItem.quoteLineItemClientShares.forEach(client => {
                            if (client.defaultSplitPercentage == null) client.defaultSplitPercentage = splitPerClient;
                        });
                    }
                }

                this.calculateTotals();

                this.addOrEditItemModalShown = true;
            },
            editItem: function (item) {
                this.currentServiceItem.originalItem = item;
                this.currentServiceItem.quoteLineItemId = item.quoteLineItemId;
                this.currentServiceItem.lineItemType = item.lineItemType;
                this.currentServiceItem.lineItemNumber = item.lineItemNumber;
                this.currentServiceItem.rateType = item.rateType;
                this.currentServiceItem.nonProductionChargeTypeId = item.nonProductionChargeTypeId;
                this.currentServiceItem.description = item.subDescription ? item.subDescription : item.description;
                this.currentServiceItem.quantity = item.quantity;
                this.currentServiceItem.unit = item.unit;
                this.currentServiceItem.unitCost = item.unitCost;
                if ((item.splitCosts === true) || (item.splitCosts === false)) this.currentServiceItem.splitCosts = item.splitCosts;
                else this.currentServiceItem.splitCosts = (item.splitPercentage != null) && (item.splitPercentage > 0) && (item.splitPercentage < 100);
                this.currentServiceItem.splitPercentage = item.splitPercentage;
                this.currentServiceItem.chargeable = item.chargeable;
                this.currentServiceItem.quantityIsPerSittingDay = item.quantityIsPerSittingDay;
                this.currentServiceItem.quoteLineItemClientShares = [];
                this.currentServiceItem.rounding = 0;
                this.currentServiceItem.courtQuoteLineItemSittingDayAssignmentCount = item.courtQuoteLineItemSittingDayAssignmentCount;
                this.currentServiceItem.courtQuoteLineItemSittingDayAssignmentString = item.courtQuoteLineItemSittingDayAssignmentString;

                //console.log(item);
                this.currentServiceItem.sittingDates = [];

                if (item.quantityIsPerSittingDay != true) {
                    if (item.courtQuoteLineItemSittingDayAssignments != null) {
                        item.courtQuoteLineItemSittingDayAssignments.forEach(item => {
                            this.currentServiceItem.sittingDates.push(
                                item.quoteLineItemClientShareId,
                                item.quoteLineItemId,
                                item.courtQuoteSittingDayId,
                                item.sittingDateString,
                            );
                        });
                        this.currentServiceItem.courtQuoteLineItemSittingDayAssignmentCount = item.courtQuoteLineItemSittingDayAssignments.length;
                        this.currentServiceItem.courtQuoteLineItemSittingDayAssignmentString = this.sittingDayAssignmentsString(item.courtQuoteLineItemSittingDayAssignments);
                    }
                }

                if (this.showClientSplitPercentages && (item.quoteLineItemClientShares == null || item.quoteLineItemClientShares.length == 0)) {
                    this.quote.clients.forEach(client => {
                        if (this.isUuidNullOrEmpty(client.quoteClientId)) client.quoteClientId = this.newUuid();

                        this.currentServiceItem.quoteLineItemClientShares.push({
                            quoteLineItemClientShareId: null,
                            quoteClientId: client.quoteClientId,
                            clientName: client.clientName,
                            clientBranchName: client.clientBranchName,
                            clientContactName: client.clientContactName,
                            defaultSplitPercentage: client.defaultSplitPercentage,
                        });
                    });
                }
                else if (item.quoteLineItemClientShares != null) {
                    item.quoteLineItemClientShares.forEach(item => {
                        this.currentServiceItem.quoteLineItemClientShares.push({
                            quoteLineItemClientShareId: item.quoteLineItemClientShareId,
                            quoteLineItemId: item.quoteLineItemId,
                            quoteClientId: item.quoteClientId,
                            clientName: item.clientName,
                            clientBranchName: item.clientBranchName,
                            clientContactName: item.clientContactName,
                            defaultSplitPercentage: item.defaultSplitPercentage,
                        });
                    });
                }

                this.currentServiceItem.amount = this.currentServiceItem.chargeable == false ? 0 :
                    this.currentServiceItem.quantityIsPerSittingDay == true ? this.currentServiceItem.splitCosts
                        ? this.quote.totalSittingDays * this.currentServiceItem.quantity * this.currentServiceItem.unitCost * (this.currentServiceItem.splitPercentage / 100)
                        : this.quote.totalSittingDays * this.currentServiceItem.quantity * this.currentServiceItem.unitCost
                        : this.currentServiceItem.splitCosts ? this.currentServiceItem.quantity * this.currentServiceItem.unitCost * (this.currentServiceItem.splitPercentage / 100) :
                            this.currentServiceItem.quantity * this.currentServiceItem.unitCost;

                this.addOrEditItemModalShown = true;
            },
            removeItem: function (item) {
                var index = this.quote.serviceItems.indexOf(item);
                if (index >= 0) {
                    this.quote.serviceItems.splice(index, 1);
                    this.calculateTotals();
                }
            },
            createOrUpdateServiceItem: function () {
                if (this.showClientSplitPercentages) {
                    var totalSplitPercentage = 0;
                    var minSplitPercentage = (100 - parseFloat(this.splitPercentageRounding));

                    this.currentServiceItem.quoteLineItemClientShares.forEach(client => {
                        totalSplitPercentage += client.defaultSplitPercentage;
                    });

                    if ((totalSplitPercentage < minSplitPercentage) || totalSplitPercentage > 100) {
                        this.showErrorsToast(["Split % does not equal 100%."]);
                        return;
                    }
                }

                if ((this.currentServiceItem.quantityIsPerSittingDay == false) && (this.currentServiceItem.sittingDates.length == 0)) {
                    this.showErrorsToast(["No Sitting Dates have been selected."]);
                    return;
                }

                this.currentServiceItem.splitPercentage = parseFloat(this.currentServiceItem.splitPercentage?.toFixed(2));

                if (this.currentServiceItem.originalItem != null) {
                    this.currentServiceItem.originalItem.quantity = Math.round(this.currentServiceItem.quantity);
                    this.currentServiceItem.originalItem.unitCost = this.currentServiceItem.unitCost;
                    this.currentServiceItem.originalItem.splitCosts = this.currentServiceItem.chargeable == true ? this.currentServiceItem.splitCosts : false;
                    this.currentServiceItem.originalItem.splitPercentage = this.currentServiceItem.splitCosts == true ? (this.currentServiceItem.splitPercentage != null ? this.currentServiceItem.splitPercentage : null) : null;
                    this.currentServiceItem.originalItem.chargeable = this.currentServiceItem.chargeable;
                    this.currentServiceItem.originalItem.quantityIsPerSittingDay = this.currentServiceItem.quantityIsPerSittingDay;
                    this.currentServiceItem.originalItem.courtQuoteLineItemSittingDayAssignments = [];
                    this.currentServiceItem.originalItem.courtQuoteLineItemSittingDayAssignmentCount = null;
                    this.currentServiceItem.originalItem.courtQuoteLineItemSittingDayAssignmentString = null;
                    this.currentServiceItem.originalItem.quoteLineItemClientShares = this.currentServiceItem.quoteLineItemClientShares;

                    if (this.currentServiceItem.nonProductionChargeTypeId != null) {
                        if (this.currentServiceItem.quantityIsPerSittingDay == true) {
                            this.currentServiceItem.originalItem.courtQuoteLineItemSittingDayAssignments = [];
                        }
                        else {
                            if (this.currentServiceItem.sittingDates != null) {
                                this.currentServiceItem.sittingDates.forEach(courtQuoteSittingDayId => {
                                    var foundSittingDate = this.nonProductionChargeSittingDayOptions.find(p => p.courtQuoteSittingDayId == courtQuoteSittingDayId);
                                    if (foundSittingDate != null) {
                                        this.currentServiceItem.originalItem.courtQuoteLineItemSittingDayAssignments.push(foundSittingDate);
                                    }
                                });

                                this.currentServiceItem.originalItem.courtQuoteLineItemSittingDayAssignmentCount = this.currentServiceItem.originalItem.courtQuoteLineItemSittingDayAssignments.length;
                                this.currentServiceItem.originalItem.courtQuoteLineItemSittingDayAssignmentString = this.sittingDayAssignmentsString(this.currentServiceItem.originalItem.courtQuoteLineItemSittingDayAssignments);
                            }
                        }
                    }

                    this.currentServiceItem.originalItem.amount = this.currentServiceItem.chargeable == false ? 0 :
                        this.currentServiceItem.quantityIsPerSittingDay == true ? this.currentServiceItem.splitCosts
                            ? this.quote.totalSittingDays * this.currentServiceItem.quantity * this.currentServiceItem.unitCost * (this.currentServiceItem.splitPercentage / 100)
                            : this.quote.totalSittingDays * this.currentServiceItem.quantity * this.currentServiceItem.unitCost
                            : this.currentServiceItem.splitCosts ? this.currentServiceItem.quantity * this.currentServiceItem.unitCost * (this.currentServiceItem.splitPercentage / 100) :
                                this.currentServiceItem.quantity * this.currentServiceItem.unitCost;
                    this.currentServiceItem.originalItem.rounding = this.currentServiceItem.rounding;
                }
                else {
                    if (this.currentServiceItem.nonProductionChargeTypeId != null) {

                        var sittingDates = [];
                        var courtQuoteLineItemSittingDayAssignmentCount = null;

                        if (this.currentServiceItem.quantityIsPerSittingDay == false) {
                            if (this.currentServiceItem.sittingDates != null) {
                                this.currentServiceItem.sittingDates.forEach(courtQuoteSittingDayId => {
                                    var foundSittingDate = this.nonProductionChargeSittingDayOptions.find(p => p.courtQuoteSittingDayId == courtQuoteSittingDayId);
                                    if (foundSittingDate != null) {
                                        sittingDates.push(foundSittingDate);
                                    }
                                });
                                courtQuoteLineItemSittingDayAssignmentCount = sittingDates.length;
                            }
                        }
                        else {
                            this.currentServiceItem.sittingDates = [];
                            courtQuoteLineItemSittingDayAssignmentCount = null;
                        }

                        //console.log(sittingDates);

                        this.quote.serviceItems.push({
                            quoteLineItemId: null,
                            lineItemType: null,
                            lineItemNumber: null,
                            rateType: null,
                            nonProductionChargeTypeId: this.currentServiceItem.nonProductionChargeTypeId,
                            description: this.currentServiceItem.description,
                            subDescription: null,
                            quantity: Math.round(this.currentServiceItem.quantity),
                            unit: this.currentServiceItem.unit,
                            unitCost: this.currentServiceItem.unitCost,
                            splitCosts: this.currentServiceItem.chargeable == true ? this.currentServiceItem.splitCosts : false,
                            splitPercentage: this.currentServiceItem.splitCosts == true ? (this.currentServiceItem.splitPercentage != null ? this.currentServiceItem.splitPercentage : null) : null,
                            chargeable: this.currentServiceItem.chargeable,
                            quantityIsPerSittingDay: this.currentServiceItem.quantityIsPerSittingDay,
                            amount: this.currentServiceItem.chargeable == false ? 0 :
                                this.currentServiceItem.quantityIsPerSittingDay == true ? this.currentServiceItem.splitCosts
                                    ? this.quote.totalSittingDays * this.currentServiceItem.quantity * this.currentServiceItem.unitCost * (this.currentServiceItem.splitPercentage / 100)
                                    : this.quote.totalSittingDays * this.currentServiceItem.quantity * this.currentServiceItem.unitCost
                                    : this.currentServiceItem.splitCosts ? this.currentServiceItem.quantity * this.currentServiceItem.unitCost * (this.currentServiceItem.splitPercentage / 100) :
                                        this.currentServiceItem.quantity * this.currentServiceItem.unitCost,
                            rounding: this.currentServiceItem.rounding,
                            courtQuoteLineItemSittingDayAssignments: sittingDates,
                            quoteLineItemClientShares: this.currentServiceItem.quoteLineItemClientShares,
                            courtQuoteLineItemSittingDayAssignmentCount: courtQuoteLineItemSittingDayAssignmentCount,
                            courtQuoteLineItemSittingDayAssignmentString: this.sittingDayAssignmentsString(sittingDates),
                            _showDetails: true,
                        });
                    }
                }
                this.calculateTotals();
                this.addOrEditItemModalShown = false;
            },
            calculateTotals: function () {
                this.serviceItemsTotal = 0.0;
                this.quote.serviceItems.forEach(item => {
                    var sittingDays = 1;
                    if (item.quantityIsPerSittingDay == true) sittingDays = this.quote.totalSittingDays;
                    if (item.courtQuoteLineItemSittingDayAssignments != null && item.courtQuoteLineItemSittingDayAssignments.length > 0) sittingDays = item.courtQuoteLineItemSittingDayAssignments.length;
                    if ((item.splitCosts !== false) && (item.splitCosts !== true)) item.splitCosts = (item.splitPercentage != null) && (item.splitPercentage > 0) && (item.splitPercentage < 100); //this is for items that got loaded from DB, they will not have that property
                    if (!item.chargeable) item.amount = 0;
                    else if (item.splitCosts) {
                        item.amount = ((sittingDays * item.quantity * item.unitCost) / 100.0) * item.splitPercentage;
                    } else item.amount = sittingDays * item.quantity * item.unitCost;
                    item.amount = Math.round(item.amount * 100) / 100.0;
                    //item.rounding = 0;
                    this.serviceItemsTotal += item.amount;
                });
                this.serviceItemsTotal = Math.round(this.serviceItemsTotal * 100) / 100.0;
                this.gstTotal = this.gstPercentage != 0 ? parseFloat((this.serviceItemsTotal * (this.gstPercentage / 100)).toFixed(2)) : 0;
                this.itemsTotal = this.serviceItemsTotal + this.gstTotal;

                this.updateServiceItemSplitPercentages();
            },
            updateServiceItemSplitPercentages: function () {
                if (this.showClientSplitPercentages) {
                    this.quote?.serviceItems?.forEach(item => {
                        if (item.quoteLineItemClientShares == null || item.quoteLineItemClientShares.length == 0) {
                            if (item.quoteLineItemClientShares == null) { item.quoteLineItemClientShares = []; }

                            this.quote.clients.forEach(client => {
                                if (this.isUuidNullOrEmpty(client.quoteClientId)) client.quoteClientId = this.newUuid();

                                item.quoteLineItemClientShares.push({
                                    quoteLineItemId: item.quoteLineItemId,
                                    quoteLineItemClientShareId: null,
                                    quoteClientId: client.quoteClientId,
                                    clientId: client.clientId,
                                    clientName: client.clientName,
                                    clientBranchId: client.clientBranchId,
                                    clientBranchName: client.clientBranchName,
                                    clientContactId: client.clientContactId,
                                    clientContactName: client.clientContactName,
                                    defaultSplitPercentage: client.defaultSplitPercentage,
                                    amount: 0,
                                    rounding: 0,
                                });
                            });
                        }
                        else {
                            this.quote.clients.forEach(client => {
                                if (this.isUuidNullOrEmpty(client.quoteClientId)) {
                                    client.quoteClientId = this.newUuid();
                                }

                                this.quote.serviceItems?.forEach(item => {
                                    var found = false;
                                    item.quoteLineItemClientShares?.forEach(clientSplit => {
                                        if (clientSplit.quoteClientId == client.quoteClientId) {
                                            found = true;
                                        }
                                    });
                                    if (!found) {
                                        item.quoteLineItemClientShares.push({
                                            quoteLineItemId: item.quoteLineItemId,
                                            quoteLineItemClientShareId: null,
                                            quoteClientId: client.quoteClientId,
                                            clientId: client.clientId,
                                            clientName: client.clientName,
                                            clientBranchId: client.clientBranchId,
                                            clientBranchName: client.clientBranchName,
                                            clientContactId: client.clientContactId,
                                            clientContactName: client.clientContactName,
                                            defaultSplitPercentage: client.defaultSplitPercentage,
                                            amount: 0,
                                            rounding: 0,
                                        });
                                    }
                                });

                                if (client.defaultSplitPercentage != null) {
                                    this.quote.serviceItems?.forEach(item => {
                                        item.quoteLineItemClientShares?.forEach(clientSplit => {
                                            if (clientSplit.quoteClientId == client.quoteClientId && clientSplit.defaultSplitPercentage == null) {
                                                clientSplit.defaultSplitPercentage = client.defaultSplitPercentage;
                                            }
                                        });
                                    });
                                }
                            });
                        }

                        var count = 0;
                        this.quote.clients.forEach(client => {
                            if (client.defaultSplitPercentage == null) count++;
                        });

                        if (count != null) {
                            var existingSplitPercentage = 0;

                            item.quoteLineItemClientShares.forEach(client => {
                                existingSplitPercentage += client.defaultSplitPercentage;
                            });

                            var remainingSplitPercentage = 100 - existingSplitPercentage;
                            var perClientRemainingSplitPercentage = remainingSplitPercentage / count;

                            item.quoteLineItemClientShares?.forEach(client => {
                                if (client.defaultSplitPercentage == null) client.defaultSplitPercentage = parseFloat(perClientRemainingSplitPercentage.toFixed(2));
                            });
                        }

                        var total = 0;
                        item.quoteLineItemClientShares?.forEach(client => {
                            var amount = parseFloat((item.amount / 100.0) * client.defaultSplitPercentage);
                            amount = parseFloat(amount.toFixed(2));
                            if (client.defaultSplitPercentage != null) client.amount = parseFloat(amount.toFixed(2));
                            total += amount;
                        });
                        total = parseFloat(total.toFixed(2));
                        item.rounding = item.amount - total;
                    });
                }
            },
            resetDefaultSplitPercentages: function () {
                this.$bvModal.msgBoxConfirm(
                    [
                        this.$createElement(
                            'div',
                            {
                                domProps:
                                {
                                    innerHTML: "<p>Are you sure you want to reset the Default Split % to equal values?</p>"
                                }
                            }
                        )
                    ],
                    {
                        title: 'Reset Default Split %',
                        size: 'md',
                        okVariant: 'warning',

                        okTitle: 'Yes, Reset',
                        cancelTitle: 'No, Do Not Reset',
                        centered: true
                    })
                    .then(value => {
                        if (value) {
                            var count = this.quote.clients?.length;
                            var splitPercentage = parseFloat((100 / count).toFixed(2));
                            this.quote.clients?.forEach(client => {
                                client.defaultSplitPercentage = splitPercentage;
                            });
                        }
                    });
            },
            clearDefaultSplitPercentages: function () {
                this.$bvModal.msgBoxConfirm(
                    [
                        this.$createElement(
                            'div',
                            {
                                domProps:
                                {
                                    innerHTML: "<p>Are you sure you want to clear the Default Split % values?</p>"
                                }
                            }
                        )
                    ],
                    {
                        title: 'Clear Default Split %',
                        size: 'md',
                        okVariant: 'warning',

                        okTitle: 'Yes, Clear',
                        cancelTitle: 'No, Do Not Clear',
                        centered: true
                    })
                    .then(value => {
                        if (value) {
                            this.quote.clients?.forEach(client => {
                                client.defaultSplitPercentage = null;
                            });
                        }
                    });
            },

            resetItemSplitPercentagesEqually: function () {
                this.$bvModal.msgBoxConfirm(
                    [
                        this.$createElement(
                            'div',
                            {
                                domProps:
                                {
                                    innerHTML: "<p>Are you sure you want to reset the Split % to equal values for ALL Items?</p>"
                                }
                            }
                        )
                    ],
                    {
                        title: 'Reset Item Split % Equally',
                        size: 'md',
                        okVariant: 'warning',

                        okTitle: 'Yes, Reset',
                        cancelTitle: 'No, Do Not Reset',
                        centered: true
                    })
                    .then(value => {
                        if (value) {
                            var count = this.quote.clients?.length;
                            var splitPercentage = parseFloat((100 / count).toFixed(2));
                            this.quote.serviceItems?.forEach(item => {
                                item.quoteLineItemClientShares?.forEach(client => {
                                    client.defaultSplitPercentage = splitPercentage;
                                });
                            });
                            this.updateServiceItemSplitPercentages();
                        }
                    });
            },
            resetItemSplitPercentages: function () {
                this.$bvModal.msgBoxConfirm(
                    [
                        this.$createElement(
                            'div',
                            {
                                domProps:
                                {
                                    innerHTML: "<p>Are you sure you want to reset the Split % to the default Client values for ALL Items?</p>"
                                }
                            }
                        )
                    ],
                    {
                        title: 'Reset All Item Split % to Default',
                        size: 'md',
                        okVariant: 'warning',

                        okTitle: 'Yes, Reset',
                        cancelTitle: 'No, Do Not Reset',
                        centered: true
                    })
                    .then(value => {
                        if (value) {
                            this.quote.serviceItems?.forEach(item => {
                                item.quoteLineItemClientShares?.forEach(client => {
                                    client.defaultSplitPercentage = null;
                                });
                            });
                            this.updateServiceItemSplitPercentages();
                        }
                    });
            },

            resetItemClientSplitPercentagesEqually: function () {
                var count = this.quote.clients?.length;
                var splitPercentage = parseFloat((100 / count).toFixed(2));
                this.currentServiceItem.quoteLineItemClientShares?.forEach(client => {
                    client.defaultSplitPercentage = splitPercentage;
                });
            },
            resetItemClientSplitPercentages: function () {
                this.currentServiceItem.quoteLineItemClientShares?.forEach(clientSplit => {
                    clientSplit.defaultSplitPercentage = null;

                    this.quote.clients?.forEach(client => {
                        if (client.quoteClientId == clientSplit.quoteClientId) { clientSplit.defaultSplitPercentage = client.defaultSplitPercentage; }
                    });
                });
            },

            apiControllerUrl: function (apiRelativeUrl) {
                var url = this.$urlHelper.getApiUrl(apiRelativeUrl);
                return url;
            },
            clientSelected: function () {
                var selectedClient = this.$refs.clientSelect.currentSelectedClients;
                if (selectedClient && selectedClient.length) selectedClient = selectedClient[0];
                if (selectedClient) {
                    this.clientManagesAssignedContracts = selectedClient.managesAssignedContracts;
                } else {
                    this.clientManagesAssignedContracts = false;
                }
                this.$refs.clientBranchValidator.reset();
                this.$refs.clientContactValidator.reset();
            },
            addClient: function () {
                this.currentClient.originalItem = null;
                this.currentClient.quoteClientId = this.newUuid();
                this.currentClient.clientId = null;
                this.currentClient.clientBranchId = null;
                this.currentClient.clientContactId = null;
                this.currentClient.defaultSplitPercentage = null;
                this.currentClient.quoteFileId = null;

                this.addOrEditClientModalShown = true;
            },
            editClient: function (item) {
                this.currentClient.originalItem = item;
                this.currentClient.quoteClientId = item.quoteClientId;
                this.currentClient.clientId = item.clientId;
                this.currentClient.clientBranchId = item.clientBranchId;
                this.currentClient.clientContactId = item.clientContactId;
                this.currentClient.defaultSplitPercentage = item.defaultSplitPercentage;
                this.currentClient.quoteFileId = item.quoteFileId;

                this.clientOptions = [{
                    id: item.clientId,
                    text: item.clientName,
                }];

                this.clientBranchOptions = [{
                    clientBranchId: item.clientBranchId,
                    name: item.clientBranchName,
                }];

                this.clientContactOptions = [{
                    contactId: item.clientContactId,
                    name: item.clientContactName,
                }];

                this.addOrEditClientModalShown = true;
            },
            createOrUpdateClient: function () {
                var selectedClient = this.$refs.clientSelect.currentSelectedClients;
                if (selectedClient && selectedClient.length) selectedClient = selectedClient[0];

                var selectedBranch = this.$refs.clientBranchSelect.currentSelectedBranches;
                if (selectedBranch && selectedBranch.length) selectedBranch = selectedBranch[0];

                var selectedClientContact = this.$refs.clientContactSelect.currentSelectedContacts;
                if (selectedClientContact && selectedClientContact.length) selectedClientContact = selectedClientContact[0];

                var client = {
                    quoteClientId: this.currentClient.quoteClientId,
                    clientId: selectedClient.id,
                    clientName: selectedClient.text,
                    clientBranchId: selectedBranch.clientBranchId,
                    clientBranchName: selectedBranch.name,
                    clientContactId: selectedClientContact.contactId,
                    clientContactName: selectedClientContact.name,
                    defaultSplitPercentage: this.currentClient.defaultSplitPercentage,
                    quoteFileId: selectedClient.quoteFileId,
                };

                if (client.defaultSplitPercentage?.length == 0) client.defaultSplitPercentage = null;

                var existingClient = false;

                this.quote.clients?.forEach(item => {
                    if (item.quoteClientId != client.quoteClientId
                        && item.clientId == client.clientId
                        && item.clientBranchId == client.clientBranchId
                        && item.clientContactId == client.clientContactId) {
                        existingClient = true;
                    }
                });

                if (existingClient == true) {
                    this.showErrorsToast(["Ordering Client already exists."]);
                }
                else {
                    if (this.currentClient.originalItem == null) {
                        this.quote.clients?.push(client);

                        if (this.showClientSplitPercentages) {
                            this.quote.serviceItems?.forEach(item => {
                                if (item.quoteLineItemClientShares == null) {
                                    item.quoteLineItemClientShares = [];
                                }

                                item.quoteLineItemClientShares.push({
                                    quoteLineItemClientShareId: client.quoteLineItemClientShareId,
                                    quoteClientId: client.quoteClientId,
                                    clientId: client.clientId,
                                    clientName: client.clientName,
                                    clientBranchId: client.clientBranchId,
                                    clientBranchName: client.clientBranchName,
                                    clientContactId: client.clientContactId,
                                    clientContactName: client.clientContactName,
                                    quoteLineItemId: client.quoteLineItemId,
                                    defaultSplitPercentage: client.defaultSplitPercentage,
                                    amount: 0,
                                });
                            });
                        }
                    }
                    else {
                        //Update client
                        var index = this.quote.clients.findIndex(e =>
                            e.quoteClientId == client.quoteClientId);
                        if (index >= 0) {
                            this.quote.clients[index].clientId = client.clientId;
                            this.quote.clients[index].clientName = client.clientName;
                            this.quote.clients[index].clientBranchId = client.clientBranchId;
                            this.quote.clients[index].clientBranchName = client.clientBranchName;
                            this.quote.clients[index].clientContactId = client.clientContactId;
                            this.quote.clients[index].clientContactName = client.clientContactName;
                            this.quote.clients[index].defaultSplitPercentage = client.defaultSplitPercentage;
                            this.quote.clients[index].quoteFileId = client.quoteFileId;
                        }

                        //Update line items
                        this.quote.serviceItems.forEach(item => {
                            if (item.quoteLineItemClientShares != null) {
                                var index = item.quoteLineItemClientShares.findIndex(e =>
                                    e.quoteClientId == client.quoteClientId);
                                if (index >= 0) {
                                    item.quoteLineItemClientShares[index].clientId = client.clientId;
                                    item.quoteLineItemClientShares[index].clientName = client.clientName;
                                    item.quoteLineItemClientShares[index].clientBranchId = client.clientBranchId;
                                    item.quoteLineItemClientShares[index].clientBranchName = client.clientBranchName;
                                    item.quoteLineItemClientShares[index].clientContactId = client.clientContactId;
                                    item.quoteLineItemClientShares[index].clientContactName = client.clientContactName;
                                }
                            }
                        });
                    }

                    this.addOrEditClientModalShown = false;

                    this.currentClient.quoteClientId = null;
                    this.currentClient.clientId = null;
                    this.currentClient.clientBranchId = null;
                    this.currentClient.clientContactId = null;
                    this.currentClient.defaultSplitPercentage = null;
                    this.currentClient.quoteFileId = null;
                }

                this.updateServiceItemSplitPercentages();
            },
            removeClient: function (item) {
                var index = this.quote.clients.indexOf(item);
                if (index >= 0) {
                    this.quote.clients.splice(index, 1);
                }

                var quoteClientId = item.quoteClientId;

                if (quoteClientId != null) {
                    this.quote.serviceItems?.forEach(item => {
                        if (item.quoteLineItemClientShares != null) {
                            for (let i = item.quoteLineItemClientShares.length - 1; i >= 0; i--) {
                                if (item.quoteLineItemClientShares[i].quoteClientId == quoteClientId) {
                                    item.quoteLineItemClientShares.splice(i, 1);
                                }
                            }
                        }
                    });
                }

                this.updateServiceItemSplitPercentages();
            },

            findCourtRegistries: function (params, success, failure) {
                var data = {
                    clientIdList: [],
                    filter: {
                        searchTerm: ''
                    },
                }
                if (typeof (params.data.term) != "undefined")
                    data.filter.searchTerm = params.data.term;

                this.quote.clients.forEach(item => {
                    data.clientIdList.push(item.clientId);
                });

                var url = this.$urlHelper.getApiUrl('CourtRegistry', 'byclientlist');
                //console.log(url);
                axios
                    .post(url, JSON.stringify(data),
                        {
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        })
                    .then(function (response) {
                        var mappedData = response.data;
                        if (mappedData != null) {
                            mappedData = $.map(mappedData, function (obj) {
                                obj.id = obj.courtRegistryId;
                                obj.text = obj.name;
                                return obj;
                            });
                        }
                        success({ results: mappedData });
                    })
                    .catch(function (error) {
                        failure(error);
                    });
            },
            courtRegistrySelected: function () {
                if (!this.isUuidNullOrEmpty(this.quote.courtRegistryId)) {
                    this.loadCourtJurisdictionsForRegistry();
                    this.loadServiceTypesForRegistry();
                    this.loadServiceDelaysForRegistry();
                    this.loadNonProductionChargeTypes();
                }
            },
            loadCourtJurisdictionsForRegistry: function () {
                if (this.quote.courtRegistryId) {
                    var url = this.$urlHelper.getApiUrl('courtjurisdiction', 'bycourtregistries');
                    axios
                        .post(url, [this.quote.courtRegistryId])
                        .then(response => {
                            this.courtJuristictionOptions = response.data;
                        })
                        .catch(ex => {
                            console.log(JSON.stringify(ex));
                            var currentErrors = this.handleAxiosException(ex);
                            this.showErrorsToast(currentErrors);
                        });
                }
            },
            loadServiceDelaysForRegistry: function () {
                this.currentErrors = [];
                if (this.quote.courtRegistryId) {
                    var url = this.$urlHelper.getApiUrl('servicedelay', 'bycourtregistry', [this.quote.courtRegistryId]);

                    axios
                        .get(url)
                        .then(response => {
                            this.serviceDelayOptions = response.data;
                        })
                        .catch(ex => {
                            console.log(JSON.stringify(ex));
                            var currentErrors = this.handleAxiosException(ex);
                            this.showErrorsToast(currentErrors);
                        });
                } else {
                    this.serviceDelayOptions = null;
                }
            },
            loadServiceTypesForRegistry: function () {
                this.currentErrors = [];
                if (this.quote.courtRegistryId) {
                    var url = this.$urlHelper.getApiUrl('servicetype', 'bycourtregistry', [this.quote.courtRegistryId]);

                    axios
                        .get(url)
                        .then(response => {
                            this.serviceTypeOptions = response.data;
                        })
                        .catch(ex => {
                            console.log(JSON.stringify(ex));
                            var currentErrors = this.handleAxiosException(ex);
                            this.showErrorsToast(currentErrors);
                        });
                } else {
                    this.serviceTypeOptions = null;
                }
            },
            loadNonProductionChargeTypes: function () {
                this.currentErrors = [];
                if (this.quote.courtRegistryId) {
                    var url = this.$urlHelper.getApiUrl('nonproductionchargetype', 'bycourtregistry', [this.quote.courtRegistryId, 'rates', true]);

                    axios
                        .get(url)
                        .then(response => {
                            this.nonProductionChargeTypeOptions = response.data;
                        })
                        .catch(ex => {
                            console.log(JSON.stringify(ex));
                            var currentErrors = this.handleAxiosException(ex);
                            this.showErrorsToast(currentErrors);
                        });
                } else {
                    this.nonProductionChargeTypeOptions = null;
                }
            },
            serviceTypeSelected: function (serviceType) {
                this.selectedServiceTypeRequiresServiceDelay = serviceType.requiresServiceDelay;
                if (!serviceType.requiresServiceDelay) {
                    this.quote.serviceDelayId = null;
                }
                this.loadDefaultLineItems();
            },
            serviceDelaySelected: function () {
                this.loadDefaultLineItems();
            },
            loadDefaultLineItems: function () {
                this.currentErrors = [];
                var currentNonProductionChargeLineItems = [];
                this.quote.serviceItems.forEach(item => {
                    if (item.nonProductionChargeTypeId != null) {
                        var serviceItem = {
                            quoteLineItemId: item.quoteLineItemId,
                            lineItemType: item.lineItemType,
                            rateType: item.rateType,
                            nonProductionChargeTypeId: item.nonProductionChargeTypeId,
                            description: item.description,
                            subDescription: item.subDescription,
                            quantity: item.quantity,
                            unit: item.unit,
                            unitCost: item.unitCost,
                            splitPercentage: item.splitCosts == true ? item.splitPercentage : null,
                            chargeable: item.chargeable,
                            quoteLineItemClientShares: [],
                            amount: 0,
                            rounding: 0,
                        };

                        currentNonProductionChargeLineItems.push(serviceItem);
                    }
                });
                var reset = true;
                if (this.quote.courtRegistryId && this.quote.serviceTypeId &&
                    (!this.selectedServiceTypeRequiresServiceDelay || this.quote.serviceDelayId)) {
                    reset = false;
                    var url = this.$urlHelper.getApiUrl('quote', 'defaultlineitems', ['court', this.quote.courtRegistryId, this.quote.serviceTypeId, this.quote.serviceDelayId]);
                    axios
                        .get(url)
                        .then(response => {
                            this.quote.serviceItems = response.data;

                            currentNonProductionChargeLineItems.forEach(item => {
                                item.quoteLineItemClientShares = [];
                                this.quote.serviceItems.push(item);
                            });
                            this.updateServiceItemQuantities();
                            this.calculateTotals();
                            this.updateServiceItemSplitPercentages();
                        })
                        .catch(ex => {
                            if (reset) {
                                this.quote.serviceItems = currentNonProductionChargeLineItems;
                                this.calculateTotals();
                                this.updateServiceItemSplitPercentages();
                            }
                            var currentErrors = this.handleAxiosException(ex);
                            this.showErrorsToast(currentErrors);
                        });
                }
                if (reset) {
                    this.quote.serviceItems = currentNonProductionChargeLineItems;
                    this.calculateTotals();
                    this.updateServiceItemSplitPercentages();
                }
            },
            nonProductionChargeTypeSelected: function (nonProductionChargeType) {
                this.currentServiceItem.nonProductionChargeTypeId = nonProductionChargeType.nonProductionChargeTypeId;
                this.currentServiceItem.description = nonProductionChargeType.name;
                this.currentServiceItem.unit = nonProductionChargeType.unit;
                this.currentServiceItem.unitCost = this.clientManagesAssignedContracts ? nonProductionChargeType.clientRate : nonProductionChargeType.externalRate;
                this.updateNPPDates();
            },
            updateNPPSittingDaySelected: function () {
                this.updateNPPDates();
            },
            updateNPPDates: function () {
                if (this.currentServiceItem.nonProductionChargeTypeId == null) {
                    this.showNPPAllSittingDays = false;
                    this.showNPPSittingDates = false;
                }
                else {
                    var npp = this.nonProductionChargeTypeOptions.find(p => p.nonProductionChargeTypeId == this.currentServiceItem.nonProductionChargeTypeId);

                    console.log('npp.relatedToDatesOptional ' + npp.relatedToDatesOptional);
                    console.log('npp.relatedToDatesRequired ' + npp.relatedToDatesRequired);

                    if (!npp.relatedToDatesOptional && !npp.relatedToDatesRequired) {
                        this.currentServiceItem.quantityIsPerSittingDay = true;
                        this.showNPPAllSittingDays = false;
                        this.showNPPSittingDates = false;
                    }
                    else if (npp.relatedToDatesOptional) {
                        this.showNPPAllSittingDays = true;
                        this.showNPPSittingDates = !this.currentServiceItem.quantityIsPerSittingDay;
                    }
                    else if (npp.relatedToDatesRequired) {
                        this.showNPPAllSittingDays = false;
                        this.currentServiceItem.quantityIsPerSittingDay = false;
                        this.showNPPSittingDates = !this.currentServiceItem.quantityIsPerSittingDay;
                    }

                    if (this.quote.sittingDates.length == 0) {
                        this.currentServiceItem.quantityIsPerSittingDay = true;
                        this.showNPPAllSittingDays = false;
                        this.showNPPSittingDates = false;
                    }
                }
            },
            printQuote: function () {
                if (this.quote.quoteFileId) {
                    window.location.replace(this.$urlHelper.getFileDownloadUrl(this.quote.quoteFileId));
                }
                else {
                    this.showSpinner = true;
                    var url = this.$urlHelper.getApiUrl('quote', 'print', [this.quote.quoteId, null]);
                    axios
                        .get(url)
                        .then(response => {
                            var quoteFileId = response.data;
                            if (!this.isUuidNullOrEmpty(quoteFileId)) {
                                this.quote.quoteFileId = quoteFileId;
                                window.location.replace(this.$urlHelper.getFileDownloadUrl(this.quote.quoteFileId));
                            }
                        })
                        .catch(ex => {
                            console.log(JSON.stringify(ex));
                            var currentErrors = this.handleAxiosException(ex);
                            this.showErrorsToast(currentErrors);
                        })
                        .finally(() => {
                            this.showSpinner = false;
                        });
                }
            },
            printNewQuote: function (item) {
                if (item.quoteFileId) {
                    window.location.replace(this.$urlHelper.getFileDownloadUrl(item.quoteFileId));
                }
                else {
                    this.showSpinner = true;
                    var url = this.$urlHelper.getApiUrl('quote', 'print', [this.quote.quoteId, item.quoteClientId]);
                    axios
                        .get(url)
                        .then(response => {
                            var quoteFileId = response.data;
                            if (!this.isUuidNullOrEmpty(quoteFileId)) {
                                item.quoteFileId = quoteFileId;
                                window.location.replace(this.$urlHelper.getFileDownloadUrl(item.quoteFileId));
                            }
                        })
                        .catch(ex => {
                            console.log(JSON.stringify(ex));
                            var currentErrors = this.handleAxiosException(ex);
                            this.showErrorsToast(currentErrors);
                        })
                        .finally(() => {
                            this.showSpinner = false;
                        });
                }
            },
            finalizeQuote: function () {
                if (this.hasSplitItemCostErrors) { return; }

                this.$bvModal.msgBoxConfirm(
                    [
                        this.$createElement(
                            'div',
                            {
                                domProps:
                                {
                                    innerHTML: "<p>Are you sure you want to finalise the estimate?</p>"
                                }
                            }
                        )
                    ],
                    {
                        title: 'Finalise Estimate',
                        size: 'md',
                        okVariant: 'success',
                        okTitle: 'Yes, Finalise Estimate',
                        cancelTitle: 'No, Do Not Finalise',
                        centered: true
                    })
                    .then(value => {
                        if (value) {
                            this.trySaveQuote('finalize');
                        }
                    });
            },
            trySaveQuote: function (actionAfterSave) {
                if (this.hasSplitItemCostErrors) { return; }

                if (this.showClientSplitPercentages) {
                    var minSplitPercentage = (100 - parseFloat(this.splitPercentageRounding));
                    var splitError = false;

                    this.quote.serviceItems?.forEach(item => {
                        var totalSplitPercentage = 0;

                        item.quoteLineItemClientShares.forEach(client => {
                            totalSplitPercentage += client.defaultSplitPercentage;
                        });

                        if ((totalSplitPercentage < minSplitPercentage) || totalSplitPercentage > 100) {
                            this.showErrorsToast(["Total Split % must be between " + minSplitPercentage + "% and 100% for Service Item '" + item.description + "'."]);
                            splitError = true;
                            return;
                        }
                    });
                    if (splitError) { return; }
                }

                this.$refs.validationObserver
                    .validate()
                    .then((isValid) => {
                        if (isValid) {
                            if (this.quote.totalSittingDays < this.quote.sittingDates.length) {
                                this.showErrorsToast(["Total Sitting Days cannot be less than the number of Sitting Dates entered."]);
                                return;
                            }

                            var url = this.$urlHelper.getApiUrl('quote', 'requestcourt');
                            var sittingDates = [];

                            this.quote.sittingDates.forEach(item => {
                                var sittingDate = {
                                    courtQuoteSittingDayId: this.isUuidNullOrEmpty(item.courtQuoteSittingDayId) ? null : item.courtQuoteSittingDayId,
                                    sittingDate: item.sittingDate,
                                    startTime: item.startTime,
                                    endTime: item.endTime,
                                };
                                sittingDates.push(sittingDate);
                            });

                            var singleClient = this.quote.clients.length == 1 || this.quote.clientId != null;
                            if (this.quote.clients.length > 1) { singleClient = false; }

                            var clients = [];

                            if (singleClient) {
                                if (this.quote.clients.length == 1) {
                                    this.quote.quoteClientId = this.quote.clients[0].quoteClientId;
                                    this.quote.clientId = this.quote.clients[0].clientId;
                                    this.quote.clientBranchId = this.quote.clients[0].clientBranchId;
                                    this.quote.clientContactId = this.quote.clients[0].clientContactId;
                                    this.quote.defaultSplitPercentage = this.quote.clients[0].defaultSplitPercentage != null ? this.quote.clients[0].defaultSplitPercentage : null;
                                }
                            }
                            else {
                                this.quote.clients.forEach(item => {
                                    var client = {
                                        quoteClientId: item.quoteClientId,
                                        clientId: item.clientId,
                                        clientBranchId: item.clientBranchId,
                                        clientContactId: item.clientContactId,
                                        defaultSplitPercentage: item.defaultSplitPercentage != null ? item.defaultSplitPercentage : null,
                                        quoteFileId: item.quoteFileId != null ? item.quoteFileId : null,
                                    };
                                    clients.push(client);
                                });
                            }

                            if (clients.length == 0 && this.quote.clientId == null) {
                                this.showErrorsToast(["At least one client must be selected."]);
                                return;
                            }

                            var serviceItems = [];
                            var lineItemNumber = 0;

                            this.quote.serviceItems?.forEach(item => {
                                var serviceItem = {
                                    quoteLineItemId: this.isUuidNullOrEmpty(item.quoteLineItemId) ? null : item.quoteLineItemId,
                                    lineItemNumber: lineItemNumber,
                                    lineItemType: item.lineItemType,
                                    rateType: item.rateType,
                                    nonProductionChargeTypeId: item.nonProductionChargeTypeId,
                                    description: item.description,
                                    subDescription: item.subDescription,
                                    quantity: item.quantity,
                                    unit: item.unit,
                                    unitCost: item.unitCost,
                                    splitPercentage: item.splitCosts == true ? item.splitPercentage : null,
                                    chargeable: item.chargeable,
                                    quantityIsPerSittingDay: item.quantityIsPerSittingDay,
                                    courtQuoteLineItemSittingDayAssignments: item.courtQuoteLineItemSittingDayAssignments,
                                    quoteLineItemClientShares: [],
                                };

                                item.quoteLineItemClientShares?.forEach((clientShare) => {
                                    if (this.isUuidNullOrEmpty(clientShare.quoteLineItemClientShareId)) { clientShare.quoteLineItemClientShareId = this.newUuid(); }
                                    if (this.isUuidNullOrEmpty(clientShare.quoteLineItemId)) { clientShare.quoteLineItemId = serviceItem.quoteLineItemId; }

                                    if (clientShare.defaultSplitPercentage == null) { clientShare.defaultSplitPercentage = 0; }
                                    else if (clientShare.defaultSplitPercentage.length == 0) { clientShare.defaultSplitPercentage = 0; }

                                    serviceItem.quoteLineItemClientShares.push({
                                        quoteLineItemClientShareId: clientShare.quoteLineItemClientShareId,
                                        quoteClientId: clientShare.quoteClientId,
                                        quoteLineItemId: clientShare.quoteLineItemId,
                                        defaultSplitPercentage: clientShare.defaultSplitPercentage,
                                    });
                                });

                                serviceItems.push(serviceItem);
                                lineItemNumber += 1;
                            });

                            var finalize = false;
                            if (actionAfterSave == 'finalize')
                                finalize = true;

                            var data = {
                                quoteId: this.quote.quoteId != null ? this.quote.quoteId : null,
                                clientList: clients,
                                clientId: this.quote.clientId != null ? this.quote.clientId : null,
                                clientBranchId: this.quote.clientBranchId != null ? this.quote.clientBranchId : null,
                                clientContactId: this.quote.clientContactId != null ? this.quote.clientContactId : null,
                                defaultSplitPercentage: this.quote.defaultSplitPercentage,
                                courtRegistryId: this.quote.courtRegistryId,
                                courtJurisdictionId: this.quote.courtJurisdictionId != null ? this.quote.courtJurisdictionId : null,
                                location: this.quote.location,
                                matterNumber: this.quote.matterNumber,
                                matterName: this.quote.matterName,
                                serviceTypeId: this.quote.serviceTypeId,
                                serviceDelayId: this.quote.serviceDelayId != null ? this.quote.serviceDelayId : null,
                                sittingDates: sittingDates,
                                serviceItems: serviceItems,
                                validityDays: this.quote.validityDays,
                                totalSittingDays: this.quote.totalSittingDays,
                                isEstimate: this.quote.isEstimate,
                                finalize: finalize,
                            }

                            if (this.newQuoteComments) {
                                data.newQuoteComments = [];
                                this.newQuoteComments.forEach((comment) => {
                                    data.newQuoteComments.push({
                                        commentHtml: comment.commentHtml,
                                        commentType: comment.commentType
                                    });
                                });
                            }

                            this.showSpinner = true;
                            axios
                                .post(url, JSON.stringify(data),
                                    {
                                        headers: {
                                            'Content-Type': 'application/json'
                                        }
                                    })
                                .then(response => {
                                    var returnUrl = this.$urlHelper.getPageUrl('Estimates/Index?initialTab=Court');

                                    if (actionAfterSave == 'save') {
                                        returnUrl = this.$urlHelper.getPageUrl('Estimates', [response.data.quoteId]);
                                    }
                                    else if (actionAfterSave == 'new') {
                                        returnUrl = this.$urlHelper.getPageUrl('Estimates/Create/CourtQuote');
                                    }
                                    else if (actionAfterSave == 'finalize') {
                                        returnUrl = this.$urlHelper.getPageUrl('Estimates', [response.data.quoteId]);
                                    }
                                    window.location.replace(returnUrl);
                                    this.showSpinner = false;
                                })
                                .catch(ex => {
                                    this.showSpinner = false;
                                    var currentErrors = this.handleAxiosException(ex);
                                    this.showErrorsToast(currentErrors);
                                });
                        }
                        else this.$refs.validationSummary.activate();
                    });
            },
            closeAndCancel: function () {
                var returnUrl = this.$urlHelper.getPageUrl('Estimates/Index?initialTab=Court');
                window.location.replace(returnUrl);
            },
            showCancelQuote: function () {
                this.cancelQuoteModalShown = true;
            },
            cancelQuote: function () {
                this.cancelQuoteModalShown = false;

                var url = this.$urlHelper.getApiUrl('Quote', 'cancel/' + this.quote.quoteId);
                var data = this.quote.quoteCancelledReason;
                axios
                    .post(url, JSON.stringify(data), {
                        headers: {
                            'Content-Type': 'application/json'
                        }
                    })
                    .then(response => {
                        var newQuote = response.data;

                        console.log(newQuote);

                        this.quote.disabledByUserAccountId = newQuote.disabledByUserAccountId;
                        this.quote.disabledDateTimeUTC = newQuote.disabledDateTimeUTC;
                        this.quote.disabledByUserAccount = newQuote.disabledByUserAccount;
                        this.quote.disabledByUserAccountDisplayName = newQuote.disabledByUserAccountDisplayName;
                        this.quote.quoteCancelledReason = newQuote.quoteCancelledReason;
                        this.quote.quoteCancelledReasonDisplayName = newQuote.quoteCancelledReasonDisplayName;

                        this.$bvToast.toast('Quote cancelled successfully', {
                            title: 'Quote Cancelled',
                            variant: 'success',
                            appendToast: true
                        });
                    })
                    .catch(ex => {
                        var errors = this.handleAxiosException(ex);
                        this.showErrorsToast(errors);
                    })
                    .finally(() => {
                        //this.grid.refresh();
                    });
            },
            renderDayCell: function (args) {
                var publicHoliday = this.formatDateTime(args.date, "yyyy-MM-DD", false, false);
                if (this.publicHolidayList.indexOf(publicHoliday) >= 0) {
                    args.element.setAttribute("class", "e-cell public-holiday");
                }
            },
            openCalendar: function () {
                this.excludeWeekends = false;
                this.resetDateValidationProviders('single');
            },
            openRangeCalendar: function () {
                this.resetDateValidationProviders('multiple');
            },
            loadPublicHolidaysForCalendar: function () {
                var relevantDate = this.currentSittingDate.sittingDate;
                if (relevantDate == null) relevantDate = this.now();
                var relevantYear = relevantDate.getFullYear();
                this.loadPublicHolidays(relevantYear);
            },
            loadPublicHolidaysForRangeCalendar: function () {
                var relevantDate = this.now();
                if (this.currentSittingDate.sittingDateRange) {
                    relevantDate = this.currentSittingDate.sittingDateRange[0];
                }
                var relevantYear = relevantDate.getFullYear();
                this.loadPublicHolidays(relevantYear);
                if (this.currentSittingDate.sittingDateRange) {
                    relevantDate = this.currentSittingDate.sittingDateRange[1];
                    relevantYear = relevantDate.getFullYear();
                    this.loadPublicHolidays(relevantYear);
                }
            },
            navigateCalendar: function (args) {
                var view = args.view;
                if (view == "Year") {
                    this.loadPublicHolidays(args.date.getFullYear());
                } else if (view == "Month") {
                    var targetMonth = args.date.getMonth();
                    var targetYear = args.date.getFullYear();
                    if (targetMonth == 0) {
                        this.loadPublicHolidays(targetYear - 1);
                    } else if (targetMonth == 11) {
                        this.loadPublicHolidays(targetYear + 1);
                    }
                }
            },

            loadPublicHolidays: async function (relevantYear) {
                if (this.publicHolidayYearsLoaded.indexOf(relevantYear) < 0) {
                    var url = this.$urlHelper.getApiUrl('PublicHoliday', 'listByYear');

                    var data = {
                        year: relevantYear,
                        courtRegistryId: this.quote.courtRegistryId,
                        interviewOrganisationId: null,
                    };

                    try {
                        let response = await axios
                            .post(url, JSON.stringify(data),
                                {
                                    headers: {
                                        'Content-Type': 'application/json'
                                    }
                                });
                        response.data.forEach(date => {
                            var publicHoliday = this.formatDateTime(date, "yyyy-MM-DD", true, true);
                            if (this.publicHolidayList.indexOf(publicHoliday) < 0) this.publicHolidayList.push(publicHoliday);
                        });
                        this.publicHolidayYearsLoaded.push(relevantYear);
                    } catch (ex) {
                        console.log(JSON.stringify(ex));
                        var currentErrors = this.handleAxiosException(ex);
                        this.showErrorsToast(currentErrors);
                    }
                }
            },

            sittingDatePopup: function (item) {
                return item.courtQuoteLineItemSittingDayAssignmentString;
            },

            toggleAllServiceItemDetails: function () {
                this.serviceItemDetailsExpanded = !this.serviceItemDetailsExpanded;

                this.quote.serviceItems?.forEach(item => {
                    item._showDetails = this.serviceItemDetailsExpanded;
                });
            },
        },
        mounted() {
            this.$nextTick(function () {
                this.calculateTotals();

                if (this.quote.quoteId != null) {
                    this.quote.serviceItems.forEach(item => {
                        item.splitCosts = item.splitPercentage != null;

                        if (item.sittingDates?.length > 0) {
                            item.quantityIsPerSittingDay = false;
                        }
                    });
                }
            });
        }
    };
</script>

<style>
    .e-calendar .e-calendar-content-table td.e-cell.public-holiday span.e-day {
        background-color: red;
        border-radius: 4px;
    }

    .e-calendar .e-calendar-content-table td.e-cell.public-holiday:hover span.e-day {
        background-color: #f78e8e;
        border-radius: 4px;
    }
</style>
